import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { LocalizedLink as Link, langFromPath, stripLangPrefix, localizePath } from "@/i18n/routing";
import { useTranslation, Trans } from "react-i18next";
import axios from "axios";
import "@/App.css";
import { Menu, X, ArrowLeft, ArrowRight, ChevronDown, Mail, Instagram, Facebook, Download, AppWindow, ChefHat, BedDouble, Bath, TreePalm, Waves, Sparkles } from "lucide-react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import useSEO from "@/hooks/useSEO";
import { PageChrome } from "@/components/site/SiteChrome";
import { CookieConsent } from "@/components/site/CookieConsent";
import { trackConversion } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VIDEO_URL =
    "https://customer-assets.emergentagent.com/job_06aedd54-467b-476b-a8a9-4a5bb8a84ce6/artifacts/k18revqc_Barefoot%20.mp4";

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_video-page-1/artifacts/2v7vadbi_e-mailhantekening_64b76e53-7295-48c0-a264-c3f8aef367e6.jpg";

const MENU_ITEMS = [
    { key: "craftDreamVilla", href: "/villas-for-sale" },
    { key: "turnkeyForSale", href: "/turnkey-villa" },
    { key: "hotelRentalProgram", href: "/hotel-rental-program" },
    { key: "destination", href: "/destination" },
    { key: "aboutUs", href: "/about" },
    { key: "contact", href: "/contact" },
    { key: "gallery", href: "/gallery" },
    { key: "press", href: "/press" },
];

const GALLERY = [
    { src: "/assets/barefoot-villa.jpg", alt: "Barefoot Luxury, villa exterior" },
    { src: "/assets/gallery/new/img-1581.webp", alt: "Luxury villas Cabo Verde, pool pergola with reed roof and open living, São Vicente" },
    { src: "/assets/barefoot-villa-2.jpg", alt: "Barefoot Luxury, villa lifestyle" },
    { src: "/assets/gallery/new/img-1445.webp", alt: "Villa rental São Vicente, outdoor kitchen and pergola with courtyard pool" },
    ...Array.from({ length: 10 }, (_, i) => ({
        src: `/assets/gallery/gallery-${String(i + 1).padStart(2, "0")}.jpg`,
        alt: `Barefoot Luxury, São Vicente · photo ${i + 1}`,
    })),
    { src: "/assets/gallery/new/img-1416.webp", alt: "Cape Verde property to buy, dining room with wooden ceiling and Danish chairs on São Vicente" },
    { src: "/assets/gallery/gallery-mindelo.jpg", alt: "Mindelo waterfront, São Vicente, Cabo Verde" },
    { src: "/assets/gallery/gallery-baia-evora.jpg", alt: "Baía de João d'Évora, São Vicente" },
    { src: "/assets/gallery/new/img-3439.webp", alt: "Real Estate Cabo Verde, concrete bathroom with rain shower and stone basins" },
    { src: "/assets/gallery/gallery-barefoot-11.jpg", alt: "Barefoot Luxury, terrace lounge with mountain view" },
    { src: "/assets/gallery/gallery-barefoot-0324.jpg", alt: "Barefoot Luxury, stone villa entrance with wooden shutters" },
    { src: "/assets/gallery/gallery-barefoot-0229.jpg", alt: "Barefoot Luxury, pergola deck above the pool" },
    { src: "/assets/gallery/gallery-barefoot-0349.jpg", alt: "Barefoot Luxury, stone villa at sunset" },
    { src: "/assets/gallery/new/img-3429.webp", alt: "Real Estate Cabo Verde, master bedroom with ceiling fan and stone accent wall on São Vicente" },
    { src: "/assets/gallery/gallery-barefoot-0547.jpg", alt: "Mindelo municipal market, São Vicente" },
    { src: "/assets/gallery/new/img-1386.webp", alt: "Luxury villas Cabo Verde, living room with concrete walls and wooden ceiling, São Vicente" },
    { src: "/assets/gallery/gallery-barefoot-0601.jpg", alt: "Local life, Mindelo street portrait" },
    { src: "/assets/gallery/new/img-1336-new.webp", alt: "Villa rental São Vicente, panoramic sofa view onto the desert bay and Atlantic ocean" },
    { src: "/assets/gallery/gallery-barefoot-0650.jpg", alt: "Fisherman preparing the daily catch" },
    { src: "/assets/gallery/new/img-3370.webp", alt: "Cape Verde property for sale, villa bathroom with outdoor shower and concrete finish" },
    { src: "/assets/gallery/gallery-barefoot-0683.jpg", alt: "Mindelo harbour, fishermen on the dock" },
    { src: "/assets/gallery/new/img-2700.webp", alt: "Villa rental São Vicente, colourful fishing boats on the Mindelo shore in Cabo Verde" },
    { src: "/assets/gallery/gallery-barefoot-0992.jpg", alt: "Local market, Mindelo, São Vicente" },
    { src: "/assets/gallery/new/img-2675.webp", alt: "Real Estate Cabo Verde, white colonial church of São Vicente with palm trees" },
    { src: "/assets/gallery/new/img-3390.webp", alt: "Cape Verde property to buy, master bedroom passageway in a turnkey Barefoot Luxury villa" },
    { src: "/assets/gallery/gallery-barefoot-1293.jpg", alt: "Barefoot Luxury, sea view through floor-to-ceiling window" },
    { src: "/assets/gallery/new/img-2715.webp", alt: "Luxury villas Cabo Verde, dramatic canyons of Santo Antão island next to São Vicente" },
    { src: "/assets/gallery/gallery-barefoot-1336.jpg", alt: "Barefoot Luxury, sunlit lounge with ceramics" },
    { src: "/assets/gallery/new/img-3382.webp", alt: "Villa rental São Vicente, double stone sinks under pendant lighting" },
    { src: "/assets/gallery/new/img-2633.webp", alt: "Cape Verde property for sale, scenic mountain road across Santo Antão with volcanic peaks" },
    { src: "/assets/gallery/gallery-barefoot-3016.jpg", alt: "Mindelo skyline, colourful houses at dusk" },
    { src: "/assets/gallery/gallery-barefoot-3062.jpg", alt: "Watching Mindelo harbour from above" },
    { src: "/assets/gallery/gallery-barefoot-aerial.jpg", alt: "Aerial top-down view of a Barefoot Luxury villa with private pool" },
    { src: "/assets/gallery/new/img-2332.webp", alt: "Cape Verde property for sale, coastal village at the foot of the São Vicente mountains" },
    { src: "https://customer-assets.emergentagent.com/job_video-page-1/artifacts/83c6g5ga_image.png", alt: "Polo Architects — villas in the desert (Wallpaper*)" },
    { src: "/assets/press-knack.jpg", alt: "Aerial view — Barefoot Luxury (Knack Weekend)" },
    { src: "/assets/press-sabato.jpg", alt: "São Vicente church (De Tijd · Sabato)" },
    { src: "/assets/press-dezeen.jpg", alt: "Villa interior — wood and glass (Dezeen)" },
    { src: "/assets/press-elledecor.jpg", alt: "Stone façade villa (Elle Decor)" },
    { src: "/assets/press-cntraveller.jpg", alt: "Open kitchen and stone wall (Condé Nast Traveller)" },
];

const MAPS = [
    {
        src: "https://customer-assets.emergentagent.com/job_video-page-1/artifacts/z7mvji2n_carte%202.png",
        alt: "Carte 2 — Connexions internationales",
        scale: 1.15,
        type: "map",
    },
    {
        src: "https://customer-assets.emergentagent.com/job_video-page-1/artifacts/lpv65xm5_carte.png",
        alt: "Carte — Archipel du Cap-Vert",
        scale: 1,
        type: "map",
    },
    {
        src: "https://customer-assets.emergentagent.com/job_video-page-1/artifacts/pdbce5cm_Carte%203.png",
        alt: "Carte 3 — Île de São Vicente",
        scale: 0.55,
        type: "map",
    },
    { src: "/assets/barefoot-villa.jpg", alt: "Barefoot Luxury, villa exterior", scale: 1, type: "photo" },
    { src: "/assets/gallery/new/img-1581.webp", alt: "Luxury villas Cabo Verde, pool pergola with reed roof and open living, São Vicente", scale: 1, type: "photo" },
    { src: "/assets/barefoot-villa-2.jpg", alt: "Barefoot Luxury, villa lifestyle", scale: 1, type: "photo" },
    { src: "/assets/gallery/new/img-1445.webp", alt: "Villa rental São Vicente, outdoor kitchen and pergola with courtyard pool", scale: 1, type: "photo" },
    ...Array.from({ length: 10 }, (_, i) => ({
        src: `/assets/gallery/gallery-${String(i + 1).padStart(2, "0")}.jpg`,
        alt: `Barefoot Luxury, São Vicente · photo ${i + 1}`,
        scale: 1,
        type: "photo",
    })),
    { src: "/assets/gallery/new/img-1416.webp", alt: "Cape Verde property to buy, dining room with wooden ceiling and Danish chairs on São Vicente", scale: 1, type: "photo" },
    { src: "/assets/gallery/gallery-mindelo.jpg", alt: "Mindelo waterfront, São Vicente, Cabo Verde", scale: 1, type: "photo" },
    { src: "/assets/gallery/gallery-baia-evora.jpg", alt: "Baía de João d'Évora, São Vicente", scale: 1, type: "photo" },
    { src: "/assets/gallery/new/img-3439.webp", alt: "Real Estate Cabo Verde, concrete bathroom with rain shower and stone basins", scale: 1, type: "photo" },
    { src: "/assets/gallery/gallery-barefoot-11.jpg", alt: "Barefoot Luxury, terrace lounge with mountain view", scale: 1, type: "photo" },
    { src: "/assets/gallery/gallery-barefoot-0324.jpg", alt: "Barefoot Luxury, stone villa entrance with wooden shutters", scale: 1, type: "photo" },
    { src: "/assets/gallery/gallery-barefoot-0229.jpg", alt: "Barefoot Luxury, pergola deck above the pool", scale: 1, type: "photo" },
    { src: "/assets/gallery/gallery-barefoot-0349.jpg", alt: "Barefoot Luxury, stone villa at sunset", scale: 1, type: "photo" },
    { src: "/assets/gallery/new/img-3429.webp", alt: "Real Estate Cabo Verde, master bedroom with ceiling fan and stone accent wall on São Vicente", scale: 1, type: "photo" },
    { src: "/assets/gallery/gallery-barefoot-0547.jpg", alt: "Mindelo municipal market, São Vicente", scale: 1, type: "photo" },
    { src: "/assets/gallery/new/img-1386.webp", alt: "Luxury villas Cabo Verde, living room with concrete walls and wooden ceiling, São Vicente", scale: 1, type: "photo" },
    { src: "/assets/gallery/gallery-barefoot-0601.jpg", alt: "Local life, Mindelo street portrait", scale: 1, type: "photo" },
    { src: "/assets/gallery/new/img-1336-new.webp", alt: "Villa rental São Vicente, panoramic sofa view onto the desert bay and Atlantic ocean", scale: 1, type: "photo" },
    { src: "/assets/gallery/gallery-barefoot-0650.jpg", alt: "Fisherman preparing the daily catch", scale: 1, type: "photo" },
    { src: "/assets/gallery/new/img-3370.webp", alt: "Cape Verde property for sale, villa bathroom with outdoor shower and concrete finish", scale: 1, type: "photo" },
    { src: "/assets/gallery/gallery-barefoot-0683.jpg", alt: "Mindelo harbour — fishermen on the dock", scale: 1, type: "photo" },
    { src: "/assets/gallery/new/img-2700.webp", alt: "Villa rental São Vicente, colourful fishing boats on the Mindelo shore in Cabo Verde", scale: 1, type: "photo" },
    { src: "/assets/gallery/gallery-barefoot-0992.jpg", alt: "Local market — Mindelo, São Vicente", scale: 1, type: "photo" },
    { src: "/assets/gallery/new/img-2675.webp", alt: "Real Estate Cabo Verde, white colonial church of São Vicente with palm trees", scale: 1, type: "photo" },
    { src: "/assets/gallery/new/img-3390.webp", alt: "Cape Verde property to buy — master bedroom passageway in a turnkey Barefoot Luxury villa", scale: 1, type: "photo" },
    { src: "/assets/gallery/gallery-barefoot-1293.jpg", alt: "Barefoot Luxury — sea view through floor-to-ceiling window", scale: 1, type: "photo" },
    { src: "/assets/gallery/new/img-2715.webp", alt: "Luxury villas Cabo Verde, dramatic canyons of Santo Antão island next to São Vicente", scale: 1, type: "photo" },
    { src: "/assets/gallery/gallery-barefoot-1336.jpg", alt: "Barefoot Luxury — sunlit lounge with ceramics", scale: 1, type: "photo" },
    { src: "/assets/gallery/new/img-3382.webp", alt: "Villa rental São Vicente — double stone sinks under pendant lighting", scale: 1, type: "photo" },
    { src: "/assets/gallery/new/img-2633.webp", alt: "Cape Verde property for sale, scenic mountain road across Santo Antão with volcanic peaks", scale: 1, type: "photo" },
    { src: "/assets/gallery/gallery-barefoot-3016.jpg", alt: "Mindelo skyline — colourful houses at dusk", scale: 1, type: "photo" },
    { src: "/assets/gallery/gallery-barefoot-3062.jpg", alt: "Watching Mindelo harbour from above", scale: 1, type: "photo" },
    { src: "/assets/gallery/gallery-barefoot-aerial.jpg", alt: "Aerial top-down view of a Barefoot Luxury villa with private pool", scale: 1, type: "photo" },
    { src: "/assets/gallery/new/img-2332.webp", alt: "Cape Verde property for sale, coastal village at the foot of the São Vicente mountains", scale: 1, type: "photo" },
    { src: "https://customer-assets.emergentagent.com/job_video-page-1/artifacts/83c6g5ga_image.png", alt: "Polo Architects — villas in the desert (Wallpaper*)", scale: 1, type: "photo" },
    { src: "/assets/press-knack.jpg", alt: "Aerial view — Barefoot Luxury (Knack Weekend)", scale: 1, type: "photo" },
    { src: "/assets/press-sabato.jpg", alt: "São Vicente church (De Tijd · Sabato)", scale: 1, type: "photo" },
    { src: "/assets/press-dezeen.jpg", alt: "Villa interior — wood and glass (Dezeen)", scale: 1, type: "photo" },
    { src: "/assets/press-elledecor.jpg", alt: "Stone façade villa (Elle Decor)", scale: 1, type: "photo" },
    { src: "/assets/press-cntraveller.jpg", alt: "Open kitchen and stone wall (Condé Nast Traveller)", scale: 1, type: "photo" },
];

const SCROLL_THRESHOLD = 10;

// Fine-tune vertical crop for portrait photos in the 4:3 landing carousel
const CAROUSEL_OBJECT_POSITION = {
    "/assets/gallery/new/img-1386.webp": "center 60%",
    "/assets/gallery/new/img-3370.webp": "center 50%",
    "/assets/gallery/new/img-3382.webp": "center 70%",
    "/assets/gallery/new/img-3390.webp": "center 55%",
    "/assets/gallery/new/img-3429.webp": "center 60%",
    "/assets/gallery/new/img-1416.webp": "center 65%",
    "/assets/gallery/new/img-1445.webp": "center 55%",
    "/assets/gallery/new/img-3439.webp": "center 65%",
    "/assets/gallery/new/img-2633.webp": "center 35%",
};

function Landing() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    useSEO("home");
    const LANG_LABEL = { en: "ENG", fr: "FR", nl: "NL", pt: "PT" };
    const [menuOpen, setMenuOpen] = useState(false);
    const [langOpen, setLangOpen] = useState(false);
    const lang = i18n.language?.slice(0, 2) || "en";
    const [scrolled, setScrolled] = useState(false);
    const [videoOpacity, setVideoOpacity] = useState(1);
    const [slide, setSlide] = useState(0);
    const [gSlide, setGSlide] = useState(0);
    const [brochureEmail, setBrochureEmail] = useState("");
    const [brochureStatus, setBrochureStatus] = useState("idle"); // idle | loading | success | error

    useEffect(() => {
        const onScroll = () => {
            const y = window.scrollY || window.pageYOffset;
            setScrolled(y > SCROLL_THRESHOLD);
            const vh = window.innerHeight || 1;
            // Fade the video out as the user scrolls past the first viewport
            const next = Math.max(0, 1 - y / (vh * 0.85));
            setVideoOpacity(next);
        };
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        if (!scrolled && menuOpen) setMenuOpen(false);
    }, [scrolled, menuOpen]);

    const goTo = (i) => setSlide(((i % MAPS.length) + MAPS.length) % MAPS.length);
    const goGTo = (i) =>
        setGSlide(((i % GALLERY.length) + GALLERY.length) % GALLERY.length);

    const submitBrochure = async (e) => {
        e.preventDefault();
        const email = brochureEmail.trim();
        if (!email || !/.+@.+\..+/.test(email)) {
            setBrochureStatus("error");
            return;
        }
        try {
            setBrochureStatus("loading");
            const lang = (i18n.language || "en").slice(0, 2).toLowerCase();
            await axios.post(`${API}/contact`, {
                name: email.split("@")[0],
                email,
                interest: "Brochure download",
                message: "Brochure auto-delivery request from landing page.",
                language: lang,
            });
            // Trigger an immediate download so the brochure is delivered even if
            // the follow-up email is delayed or filtered to spam.
            const link = document.createElement("a");
            link.href = `${API}/brochure?lang=${lang}`;
            link.rel = "noopener";
            document.body.appendChild(link);
            link.click();
            link.remove();
            trackConversion("brochure_download", { language: lang, form_location: "landing" });
            setBrochureStatus("success");
            setBrochureEmail("");
        } catch (err) {
            console.error(err);
            setBrochureStatus("error");
        }
    };

    return (
        <main data-testid="landing-page" className="relative">
            {/* Fixed full-screen background video */}
            <video
                data-testid="hero-video"
                className="fixed inset-x-0 top-0 w-screen h-[85vh] md:h-screen object-cover -z-10 transition-opacity duration-300 ease-out bg-[#1a1a18]"
                style={{ opacity: videoOpacity }}
                autoPlay
                muted
                loop
                playsInline
                {...{ "webkit-playsinline": "true", "x5-playsinline": "true", "x5-video-player-type": "h5", "x5-video-player-fullscreen": "false" }}
                controls={false}
                disablePictureInPicture
                disableRemotePlayback
                preload="metadata"
                poster="/assets/hero/hero-poster.jpg"
                ref={(el) => {
                    if (!el) return;
                    el.muted = true;
                    const p = el.play();
                    if (p && typeof p.catch === "function") p.catch(() => {});
                }}
            >
                <source src="/assets/hero/hero-mobile.mp4" type="video/mp4" media="(max-width: 767px)" />
                <source src="/assets/hero/hero-desktop.mp4" type="video/mp4" />
            </video>

            {/* Hero spacer, lets the fixed video remain visible */}
            <section
                data-testid="hero-section"
                className="h-[85vh] md:h-screen w-full"
                aria-hidden="true"
            />

            {/* Localisation section: carousel left, text right */}
            <section
                id="localisation"
                data-testid="localisation-section"
                className="relative bg-white pt-10 md:pt-16 pb-6 md:pb-10"
            >
                {/* Cursive headline — fully centered on viewport */}
                <div className="w-full mb-6 md:mb-10 flex justify-center px-6">
                    <img
                        src="https://customer-assets.emergentagent.com/job_video-page-1/artifacts/vd7eljyg_Capture%20d%E2%80%99e%CC%81cran%202026-06-30%20a%CC%80%2019.33.40.png"
                        alt="Luxury villas Cabo Verde — Barefoot Luxury on São Vicente"
                        data-testid="luxury-quote"
                        className="w-full max-w-md md:max-w-lg h-auto mix-blend-multiply"
                        loading="lazy"
                    />
                </div>

                <div className="max-w-[1600px] mx-auto pl-4 md:pl-8 lg:pl-12 pr-6 md:pr-12 lg:pr-20">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-stretch">
                        {/* Carousel — LEFT */}
                        <div
                            data-testid="maps-carousel"
                            className="lg:col-span-7 flex flex-col"
                        >
                            <div className="relative">
                                <div className="relative aspect-[4/3] w-full overflow-hidden group">
                                    {MAPS.map((m, i) => (
                                        <img
                                            key={m.src}
                                            src={m.src}
                                            alt={m.alt}
                                            data-testid={`carousel-slide-${i}`}
                                            style={{
                                                transform: `scale(${m.scale})`,
                                                objectPosition: CAROUSEL_OBJECT_POSITION[m.src] || "center",
                                            }}
                                            className={`absolute inset-0 w-full h-full transition-opacity duration-700 ease-out ${
                                                m.type === "photo" ? "object-cover" : "object-contain"
                                            } ${
                                                i === slide
                                                    ? "opacity-100"
                                                    : "opacity-0"
                                            }`}
                                        />
                                    ))}

                                    {/* Arrows overlaid on the photo */}
                                    <button
                                        type="button"
                                        onClick={() => goTo(slide - 1)}
                                        aria-label="Carte précédente"
                                        data-testid="carousel-prev"
                                        className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-white/60 backdrop-blur-sm text-[#1a1a18] hover:bg-[#1a1a18] hover:text-white transition-colors rounded-full"
                                    >
                                        <ArrowLeft size={18} strokeWidth={1.4} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => goTo(slide + 1)}
                                        aria-label="Carte suivante"
                                        data-testid="carousel-next"
                                        className="absolute right-3 md:right-4 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-white/60 backdrop-blur-sm text-[#1a1a18] hover:bg-[#1a1a18] hover:text-white transition-colors rounded-full"
                                    >
                                        <ArrowRight size={18} strokeWidth={1.4} />
                                    </button>
                                </div>

                                {/* Pagination indicator, minimalist counter */}
                                <div
                                    data-testid="carousel-dots"
                                    className="mt-5 flex items-center justify-center gap-4"
                                    aria-label="Carousel pagination"
                                >
                                    <span className="text-[11px] tracking-[0.28em] text-[#1a1a18] tabular-nums">
                                        {String(slide + 1).padStart(2, "0")}
                                    </span>
                                    <span className="h-px w-16 bg-[#1a1a18]/25 relative overflow-hidden">
                                        <span
                                            className="absolute inset-y-0 left-0 bg-[#1a1a18] transition-all duration-500 ease-out"
                                            style={{ width: `${((slide + 1) / MAPS.length) * 100}%` }}
                                        />
                                    </span>
                                    <span className="text-[11px] tracking-[0.28em] text-[#1a1a18]/50 tabular-nums">
                                        {String(MAPS.length).padStart(2, "0")}
                                    </span>
                                </div>
                            </div>

                            {/* Discover your villa — under carousel */}
                            <div className="mt-10 lg:mt-auto lg:pt-12">
                                <p className="text-[14px] md:text-[16px] uppercase tracking-[0.28em] font-bold text-[#1a1a18]">
                                    {t("landing.discoverYourVilla")}
                                </p>
                                <div className="mt-4 h-px w-full bg-[#1a1a18]/20" />
                                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <Link
                                        to="/villas-for-sale"
                                        data-testid="enquiry-cta"
                                        className="group w-full inline-flex items-center justify-center gap-3 bg-[#b59565] hover:bg-[#9a7d52] text-[#1a1a18] px-6 py-4 rounded-full text-[12px] uppercase tracking-[0.24em] font-bold transition-all duration-300 text-center leading-tight"
                                    >
                                        <span className="relative">
                                            {t("nav.craftDreamVilla")}
                                            <span className="absolute left-0 -bottom-0.5 h-px w-full bg-[#1a1a18] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500 ease-out" />
                                        </span>
                                        <ArrowRight
                                            size={14}
                                            strokeWidth={1.8}
                                            className="flex-shrink-0 transition-transform duration-300 group-hover:translate-x-1"
                                        />
                                    </Link>
                                    <Link
                                        to="/turnkey-villa"
                                        data-testid="turnkey-cta"
                                        className="group w-full inline-flex items-center justify-center gap-3 bg-[#b59565] hover:bg-[#9a7d52] text-[#1a1a18] px-6 py-4 rounded-full text-[12px] uppercase tracking-[0.24em] font-bold transition-all duration-300 text-center leading-tight"
                                    >
                                        <span className="relative">
                                            {t("nav.turnkeyForSale")}
                                            <span className="absolute left-0 -bottom-0.5 h-px w-full bg-[#1a1a18] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500 ease-out" />
                                        </span>
                                        <ArrowRight
                                            size={14}
                                            strokeWidth={1.8}
                                            className="flex-shrink-0 transition-transform duration-300 group-hover:translate-x-1"
                                        />
                                    </Link>
                                </div>
                            </div>
                        </div>

                        {/* Text — RIGHT */}
                        <div
                            data-testid="localisation-text"
                            className="lg:col-span-5 lg:pl-4 lg:mt-20 flex flex-col"
                        >
                            <p
                                className="text-[#1a1a18] text-3xl md:text-4xl lg:text-[42px] leading-[1.15] tracking-tight"
                                style={{
                                    fontFamily:
                                        '"Cormorant Garamond", "Times New Roman", serif',
                                }}
                            >
                                <Trans i18nKey="landing.intro.lead" components={{ i: <span className="italic" />, br: <br /> }} />
                            </p>
                            <p className="mt-8 text-[#3d3b36] text-base md:text-[17px] leading-[1.85] font-light">
                                {t("landing.intro.p1")}
                            </p>
                            <p className="mt-6 text-[#3d3b36] text-base md:text-[17px] leading-[1.85] font-light">
                                {t("landing.intro.p2")}
                            </p>
                            <div className="mt-10 lg:mt-auto lg:pt-12">
                                <p
                                    data-testid="rental-program"
                                    className="text-[14px] md:text-[16px] uppercase tracking-[0.28em] font-bold text-[#1a1a18]"
                                >
                                    {t("landing.exploreRentalProgram")}
                                </p>
                                <div className="mt-4 h-px w-full bg-[#1a1a18]/20" />
                                <Link
                                    to="/hotel-rental-program"
                                    data-testid="hospitality-cta"
                                    className="group mt-4 w-full inline-flex items-center justify-center gap-3 bg-[#b59565] hover:bg-[#9a7d52] text-[#1a1a18] px-6 py-4 rounded-full text-[12px] uppercase tracking-[0.24em] font-bold transition-all duration-300 text-center leading-tight"
                                >
                                    <span className="relative">
                                        {t("landing.hospitalityCta")}
                                        <span className="absolute left-0 -bottom-0.5 h-px w-full bg-[#1a1a18] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500 ease-out" />
                                    </span>
                                    <ArrowRight
                                        size={14}
                                        strokeWidth={1.8}
                                        className="flex-shrink-0 transition-transform duration-300 group-hover:translate-x-1"
                                    />
                                </Link>
                            </div>
                        </div>
                    </div>

                    {/* Full-width villa photo below the 3 CTA buttons */}
                    <div
                        data-testid="full-width-villa"
                        className="mt-16 md:mt-24 relative left-1/2 -translate-x-1/2 w-screen"
                    >
                        <img
                            src="/assets/full-width-villa.jpg"
                            alt="Cape Verde property for sale — luxury villa amid the São Vicente landscape"
                            loading="lazy"
                            className="w-full h-[55vh] md:h-[75vh] lg:h-[85vh] object-cover"
                        />
                    </div>

                    {/* Villa photo with text on the left */}
                    <div className="mt-20 md:mt-28 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                        <div
                            data-testid="villa-photo-text"
                            className="lg:col-start-2 lg:col-span-5 lg:pl-2 lg:pr-4 flex flex-col gap-12"
                        >
                            <div className="order-2 lg:order-1">
                                <p className="text-[#1a1a18] text-base md:text-[17px] leading-[1.85] font-semibold">
                                    {t("landing.reflection.p1")}
                                </p>
                                <p className="mt-6 text-[#3d3b36] text-base md:text-[17px] leading-[1.85] font-light">
                                    {t("landing.reflection.p2")}
                                </p>
                            </div>

                            <img
                                src="/assets/barefoot-villa-2.jpg"
                                alt="Villa rental São Vicente — Barefoot Luxury lifestyle on Cabo Verde"
                                data-testid="villa-photo-2"
                                loading="lazy"
                                className="order-1 lg:order-2 w-full h-auto object-cover"
                            />
                        </div>

                        <div className="lg:col-start-8 lg:col-span-5 lg:pl-4 space-y-12">
                            <img
                                src="/assets/barefoot-villa.jpg"
                                alt="Cape Verde property to buy — Barefoot Luxury villa on São Vicente"
                                data-testid="villa-photo"
                                loading="lazy"
                                className="w-full h-auto object-cover"
                            />

                            <div data-testid="villa-photo-2-text">
                                <p className="text-[#1a1a18] text-base md:text-[17px] leading-[1.85] font-semibold">
                                    {t("landing.welcome.p1")}
                                </p>
                                <p className="mt-6 text-[#3d3b36] text-base md:text-[17px] leading-[1.85] font-light">
                                    {t("landing.welcome.p2")}{" "}
                                    <span className="italic">
                                        {t("landing.welcome.p2End")}
                                    </span>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Full-width gallery carousel */}
            <section
                id="gallery"
                data-testid="gallery-section"
                className="relative bg-white pb-8 md:pb-12"
            >
                {/* Features icons row above carousel */}
                <div data-testid="features-icons" className="max-w-6xl mx-auto px-6 md:px-10 lg:px-16 pt-4 md:pt-6 pb-14 md:pb-20">
                    <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-x-6 gap-y-10 md:gap-y-12">
                        {["windows", "kitchen", "bedrooms", "bathrooms", "terraces", "pool", "finish"].map((key, i) => (
                            <li
                                key={key}
                                data-testid={`landing-feature-${i}`}
                                className="flex flex-col items-center text-center"
                            >
                                <img
                                    src={`/assets/villa-24/icons/${key}.svg`}
                                    alt=""
                                    aria-hidden="true"
                                    className="h-16 md:h-20 w-auto mb-5 select-none"
                                    draggable="false"
                                />
                                <p className="text-[12px] md:text-[13px] leading-[1.5] text-[#1a1a18] font-light lowercase">
                                    {t(`villasForSale.features.${key}`)}
                                </p>
                            </li>
                        ))}
                    </ul>
                </div>
                <div
                    data-testid="gallery-carousel"
                    className="relative w-full aspect-[16/9] md:aspect-[16/8] overflow-hidden bg-[#f3ead9] group"
                >
                    {GALLERY.map((g, i) => (
                        <img
                            key={g.src}
                            src={g.src}
                            alt={g.alt}
                            loading={i === 0 ? "eager" : "lazy"}
                            data-testid={`gallery-slide-${i}`}
                            className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-1000 ease-out ${
                                i === gSlide ? "opacity-100" : "opacity-0"
                            }`}
                        />
                    ))}

                    {/* Counter — bottom right */}
                    <div className="absolute bottom-6 right-6 md:bottom-8 md:right-10 text-white drop-shadow-md text-[11px] uppercase tracking-[0.3em] font-light">
                        <span
                            className="text-2xl mr-1 align-middle"
                            style={{
                                fontFamily:
                                    '"Cormorant Garamond", "Times New Roman", serif',
                            }}
                        >
                            {String(gSlide + 1).padStart(2, "0")}
                        </span>
                        / {String(GALLERY.length).padStart(2, "0")}
                    </div>

                    {/* Arrows on photo */}
                    <button
                        type="button"
                        onClick={() => goGTo(gSlide - 1)}
                        aria-label="Image précédente"
                        data-testid="gallery-prev"
                        className="absolute left-4 md:left-6 top-1/2 -translate-y-1/2 w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-white/60 backdrop-blur-sm text-[#1a1a18] hover:bg-[#1a1a18] hover:text-white transition-colors rounded-full opacity-0 group-hover:opacity-100 focus:opacity-100"
                    >
                        <ArrowLeft size={20} strokeWidth={1.4} />
                    </button>
                    <button
                        type="button"
                        onClick={() => goGTo(gSlide + 1)}
                        aria-label="Image suivante"
                        data-testid="gallery-next"
                        className="absolute right-4 md:right-6 top-1/2 -translate-y-1/2 w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-white/60 backdrop-blur-sm text-[#1a1a18] hover:bg-[#1a1a18] hover:text-white transition-colors rounded-full opacity-0 group-hover:opacity-100 focus:opacity-100"
                    >
                        <ArrowRight size={20} strokeWidth={1.4} />
                    </button>
                </div>
            </section>

            {/* Contact us mailto button */}
            <section
                data-testid="contact-us-section"
                className="bg-white pt-6 md:pt-8 pb-14 md:pb-20"
            >
                <div className="max-w-2xl mx-auto px-6 md:px-10 text-center">
                    <a
                        href="mailto:stef@barefootluxury.world"
                        data-testid="contact-us-btn"
                        className="group inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[#1a1a18] text-[#f5ecd9] text-[11px] md:text-[12px] uppercase tracking-[0.32em] hover:bg-[#b59565] hover:text-[#1a1a18] transition-colors duration-500"
                    >
                        <Mail size={16} strokeWidth={1.6} />
                        {t("landing.contactUs")}
                        <ArrowRight
                            size={14}
                            strokeWidth={1.6}
                            className="transition-transform duration-500 group-hover:translate-x-1"
                        />
                    </a>
                </div>
            </section>

            {/* Brochure download section */}
            <section
                id="brochure"
                data-testid="brochure-section"
                className="bg-white py-10 md:py-14 border-t border-[#e5d9bf]"
            >
                <div className="max-w-2xl mx-auto px-6 md:px-10 text-center">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#f3ead9] mb-8">
                        <Download size={18} strokeWidth={1.4} className="text-[#7a4a3a]" />
                    </div>
                    <h2
                        className="text-[#1a1a18] text-4xl md:text-5xl lg:text-[52px] leading-[1.05] tracking-tight"
                        style={{
                            fontFamily:
                                '"Cormorant Garamond", "Times New Roman", serif',
                        }}
                    >
                        {t("landing.brochure.title")}
                    </h2>
                    <p className="mt-5 text-[#5c5a55] text-base md:text-[17px] leading-[1.75] font-light">
                        {t("landing.brochure.subtitle")}
                    </p>

                    <form
                        onSubmit={submitBrochure}
                        data-testid="brochure-form"
                        className="mt-10 flex flex-col sm:flex-row items-stretch gap-3 sm:gap-0"
                    >
                        <input
                            type="email"
                            value={brochureEmail}
                            onChange={(e) => {
                                setBrochureEmail(e.target.value);
                                if (brochureStatus !== "idle")
                                    setBrochureStatus("idle");
                            }}
                            data-testid="brochure-email-input"
                            placeholder={t("landing.brochure.placeholder")}
                            required
                            aria-label="Email address"
                            className="flex-1 bg-transparent border border-[#e5d9bf] sm:border-r-0 px-5 py-4 text-[15px] text-[#1a1a18] placeholder:text-[#9a958a] focus:outline-none focus:border-[#7a4a3a] transition-colors"
                        />
                        <button
                            type="submit"
                            data-testid="brochure-submit"
                            disabled={brochureStatus === "loading"}
                            className="inline-flex items-center justify-center gap-2 bg-[#1a1a18] hover:bg-[#7a4a3a] disabled:opacity-60 text-white px-7 py-4 text-[12px] uppercase tracking-[0.25em] font-light transition-colors"
                        >
                            {brochureStatus === "loading"
                                ? t("landing.brochure.sending")
                                : brochureStatus === "success"
                                  ? t("landing.brochure.sent")
                                  : t("landing.brochure.cta")}
                            {brochureStatus !== "loading" &&
                                brochureStatus !== "success" && (
                                    <ArrowRight size={14} strokeWidth={1.4} />
                                )}
                        </button>
                    </form>

                    {brochureStatus === "success" && (
                        <div className="mt-5 flex flex-col items-start gap-3">
                            <p
                                data-testid="brochure-success"
                                className="text-[13px] text-[#7a4a3a] tracking-wide"
                            >
                                {t("landing.brochure.success")}
                            </p>
                            <a
                                data-testid="brochure-download-link"
                                href={`${API}/brochure?lang=${(i18n.language || "en").slice(0, 2).toLowerCase()}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 text-[12px] tracking-[0.15em] uppercase text-[#1a1a18] border-b border-[#1a1a18] pb-1 hover:opacity-70 transition-opacity"
                            >
                                <Download size={14} strokeWidth={1.4} />
                                {t("landing.brochure.downloadNow")}
                            </a>
                        </div>
                    )}
                    {brochureStatus === "error" && (
                        <p
                            data-testid="brochure-error"
                            className="mt-5 text-[13px] text-[#b04a3a] tracking-wide"
                        >
                            {t("landing.brochure.error")}
                        </p>
                    )}
                </div>
            </section>

            {/* Footer with logo and legal links */}
            <footer
                data-testid="site-footer"
                className="bg-white pt-8 pb-16 border-t border-[#e5d9bf]"
            >
                <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 flex flex-col items-center text-center">
                    <img
                        src="/assets/logo-footer.png"
                        alt="Cabo Verde"
                        data-testid="footer-logo"
                        className="h-20 md:h-24 w-auto mb-10"
                        loading="lazy"
                    />
                    <nav
                        data-testid="footer-legal"
                        aria-label="Mentions légales"
                        className="flex flex-col sm:flex-row flex-wrap items-center justify-center divide-y sm:divide-y-0 sm:divide-x divide-[#cfc6b3] text-[11px] uppercase tracking-[0.25em] font-light text-[#5c5a55]"
                    >
                        {[
                            { key: "cookie", href: "/cookie-policy" },
                            { key: "privacy", href: "/privacy-policy" },
                            { key: "terms", href: "/terms-and-conditions" },
                            { key: "disclaimer", href: "/disclaimer" },
                        ].map((link) => {
                            const isRoute = link.href.startsWith("/");
                            const cls = "px-5 py-3 hover:text-[#1a1a18] transition-colors text-center";
                            return isRoute ? (
                                <Link
                                    key={link.key}
                                    to={link.href}
                                    data-testid={`footer-link-${link.key}`}
                                    className={cls}
                                >
                                    {t(`footer.${link.key}`)}
                                </Link>
                            ) : (
                                <a
                                    key={link.key}
                                    href={link.href}
                                    data-testid={`footer-link-${link.key}`}
                                    className={cls}
                                >
                                    {t(`footer.${link.key}`)}
                                </a>
                            );
                        })}
                        <button
                            type="button"
                            data-testid="footer-cookie-preferences"
                            onClick={() => window.dispatchEvent(new Event("open-cookie-preferences"))}
                            className="px-5 py-3 uppercase tracking-[0.25em] hover:text-[#1a1a18] transition-colors text-center"
                        >
                            {t("footer.cookiePreferences")}
                        </button>
                    </nav>
                </div>
            </footer>
            <header
                data-testid="top-banner"
                aria-hidden={!scrolled}
                className={`fixed top-0 left-0 right-0 z-20 bg-[#f3ead9] grid grid-cols-[1fr_auto_1fr] items-center px-4 md:px-10 lg:px-14 border-b border-[#e5d9bf] h-[68px] md:h-[84px] transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                    scrolled
                        ? "translate-y-0 pointer-events-auto"
                        : "-translate-y-full pointer-events-none"
                }`}
            >
                {/* Left side: Menu + Language dropdown */}
                <div className="flex items-center gap-3 md:gap-6 min-w-0 justify-self-start">
                    <button
                        type="button"
                        data-testid="menu-button"
                        aria-label="Ouvrir le menu"
                        aria-expanded={menuOpen}
                        onClick={() => {
                            setMenuOpen((v) => !v);
                            setLangOpen(false);
                        }}
                        className="group flex items-center gap-2 text-[#1a1a18] hover:text-[#7a4a3a] transition-colors py-2"
                    >
                        {menuOpen ? (
                            <X size={18} strokeWidth={1.4} />
                        ) : (
                            <Menu size={18} strokeWidth={1.4} />
                        )}
                        <span className="hidden sm:inline text-[11px] md:text-[12px] uppercase tracking-[0.22em] md:tracking-[0.28em] font-light">
                            {t("common.menu")}
                        </span>
                    </button>

                    {/* Language dropdown */}
                    <div className="relative">
                        <button
                            type="button"
                            data-testid="lang-toggle"
                            aria-label="Choisir la langue"
                            aria-expanded={langOpen}
                            onClick={() => {
                                setLangOpen((v) => !v);
                                setMenuOpen(false);
                            }}
                            className="group flex items-center gap-2 text-[#1a1a18] hover:text-[#7a4a3a] transition-colors py-2"
                        >
                            <span className="text-[11px] md:text-[12px] uppercase tracking-[0.28em] font-light">
                                {LANG_LABEL[lang] || "ENG"}
                            </span>
                            <ChevronDown
                                size={14}
                                strokeWidth={1.4}
                                className={`transition-transform duration-300 ${langOpen ? "rotate-180" : ""}`}
                            />
                        </button>
                        {langOpen && (
                            <ul
                                data-testid="lang-dropdown"
                                className="absolute top-full left-0 mt-3 min-w-[110px] bg-[#f3ead9] border border-[#e5d9bf] shadow-md py-1 z-30"
                            >
                                {["en", "nl", "fr", "pt"].map((l) => (
                                    <li key={l}>
                                        <button
                                            type="button"
                                            data-testid={`lang-option-${l}`}
                                            onClick={() => {
                                                i18n.changeLanguage(l);
                                                setLangOpen(false);
                                                navigate(localizePath(stripLangPrefix(location.pathname), l), { replace: true });
                                            }}
                                            className={`w-full text-left px-4 py-2 text-[11px] uppercase tracking-[0.28em] font-light transition-colors ${
                                                lang === l
                                                    ? "text-[#7a4a3a] bg-[#e5d9bf]/40"
                                                    : "text-[#1a1a18] hover:bg-[#e5d9bf]/40"
                                            }`}
                                        >
                                            {LANG_LABEL[l]}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>

                <div
                    data-testid="banner-logo"
                    role="img"
                    aria-label="Barefoot Luxury"
                    className="justify-self-center w-28 sm:w-40 md:w-64 lg:w-72 h-[68px] md:h-[84px] mix-blend-multiply pointer-events-none overflow-hidden"
                    style={{
                        backgroundImage: `url(${LOGO_URL})`,
                        backgroundRepeat: "no-repeat",
                        backgroundSize: "auto 128%",
                        backgroundPosition: "center -12px",
                    }}
                />

                {/* Right side: social icons */}
                <nav
                    data-testid="social-nav"
                    aria-label="Réseaux sociaux & contact"
                    className="flex items-center justify-self-end gap-3 md:gap-5 text-[#1a1a18] min-w-0"
                >
                    <a
                        href="mailto:stef@barefootluxury.world"
                        data-testid="social-mail"
                        aria-label="Nous écrire par email"
                        className="hover:text-[#7a4a3a] transition-colors"
                    >
                        <Mail size={18} strokeWidth={1.4} />
                    </a>
                    <a
                        href="https://wa.me/32475838300"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="social-whatsapp"
                        aria-label="WhatsApp"
                        className="hover:text-[#7a4a3a] transition-colors"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 32 32"
                            width="18"
                            height="18"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                        >
                            <path d="M27 15.5c0 6.075-4.925 11-11 11a10.96 10.96 0 0 1-5.252-1.341L5 27l1.86-5.65A10.96 10.96 0 0 1 5 15.5C5 9.425 9.925 4.5 16 4.5s11 4.925 11 11Z" />
                            <path d="M12 11.5c0 3.6 4.4 8 8 8 .8 0 1.5-.7 1.5-1.5 0-.4-.2-.7-.5-.9l-1.8-.9c-.3-.2-.7-.1-.9.2l-.5.6c-1.4-.4-2.5-1.5-2.9-2.9l.6-.5c.3-.2.4-.6.2-.9l-.9-1.8c-.2-.3-.5-.5-.9-.5-.8 0-1.5.7-1.5 1.5Z" />
                        </svg>
                    </a>
                    <a
                        href="https://www.instagram.com/barefootluxury_saovicente/"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="social-instagram"
                        aria-label="Instagram"
                        className="hover:text-[#7a4a3a] transition-colors"
                    >
                        <Instagram size={18} strokeWidth={1.4} />
                    </a>
                    <a
                        href="https://www.facebook.com/p/Barefoot-Luxury-S%C3%A3o-Vicente-61564999997147/"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="social-facebook"
                        aria-label="Facebook"
                        className="hover:text-[#7a4a3a] transition-colors"
                    >
                        <Facebook size={18} strokeWidth={1.4} />
                    </a>
                </nav>
            </header>

            {/* Menu drawer */}
            <div
                data-testid="menu-drawer"
                className={`fixed top-[68px] md:top-[84px] left-0 right-0 z-10 bg-[#f3ead9]/95 backdrop-blur-md border-b border-[#e5d9bf] transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                    scrolled && menuOpen
                        ? "opacity-100 translate-y-0 pointer-events-auto"
                        : "opacity-0 -translate-y-4 pointer-events-none"
                }`}
            >
                <ul className="max-w-4xl mx-auto px-6 md:px-10 py-8 flex flex-col gap-5">
                    {MENU_ITEMS.map((item) => {
                        const isRoute = item.href.startsWith("/");
                        const label = t(`nav.${item.key}`);
                        const linkClass =
                            "text-[#1a1a18] text-2xl md:text-3xl font-light tracking-wide hover:text-[#7a4a3a] transition-colors";
                        const linkStyle = {
                            fontFamily:
                                '"Cormorant Garamond", "Times New Roman", serif',
                        };
                        const testId = `menu-link-${item.key}`;
                        return (
                            <li key={item.key}>
                                {isRoute ? (
                                    <Link
                                        to={item.href}
                                        data-testid={testId}
                                        onClick={() => setMenuOpen(false)}
                                        className={linkClass}
                                        style={linkStyle}
                                    >
                                        {label}
                                    </Link>
                                ) : (
                                    <a
                                        href={item.href}
                                        data-testid={testId}
                                        onClick={() => setMenuOpen(false)}
                                        className={linkClass}
                                        style={linkStyle}
                                    >
                                        {label}
                                    </a>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </div>

            {/* Floating Contact CTA — links to contact form */}
            <Link
                to="/contact"
                data-testid="contact-fab"
                aria-label="Contact us"
                className="fixed bottom-5 right-5 z-50 group hidden sm:flex items-center gap-2.5 sm:gap-3 bg-[#f3ead9] hover:bg-[#ebdfc0] text-[#1a1a18] border border-[#e5d9bf] rounded-full shadow-[0_10px_30px_rgba(26,26,24,0.18)] transition-all hover:scale-[1.03] pl-4 pr-5 py-3 sm:pl-5 sm:pr-6 sm:py-3.5"
            >
                <Mail
                    size={16}
                    strokeWidth={1.6}
                    className="text-[#1a1a18] sm:hidden"
                    aria-hidden="true"
                />
                <Mail
                    size={18}
                    strokeWidth={1.6}
                    className="text-[#1a1a18] hidden sm:block"
                    aria-hidden="true"
                />
                <span className="sm:hidden text-[11px] uppercase tracking-[0.2em] font-medium whitespace-nowrap">
                    {t("fab.mobile")}
                </span>
                <span className="hidden sm:inline text-[11px] md:text-[12px] uppercase tracking-[0.22em] font-medium leading-tight whitespace-nowrap">
                    {t("fab.desktop")}
                </span>
                <ArrowRight
                    size={12}
                    strokeWidth={1.8}
                    className="text-[#1a1a18] sm:hidden transition-transform duration-500 group-hover:translate-x-1"
                    aria-hidden="true"
                />
                <ArrowRight
                    size={14}
                    strokeWidth={1.8}
                    className="text-[#1a1a18] hidden sm:block transition-transform duration-500 group-hover:translate-x-1"
                    aria-hidden="true"
                />
            </Link>
        </main>
    );
}

function Press() {
    const { t } = useTranslation();
    useSEO("press");
    return (
        <PageChrome><main
            data-testid="press-page"
            className="min-h-screen bg-[#fdfbf7] text-[#1a1a18]"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-32 md:pt-44 pb-24 md:pb-32">
                <Link
                    to="/"
                    data-testid="press-back-link"
                    className="inline-flex items-center gap-2 text-[11px] md:text-[12px] uppercase tracking-[0.28em] text-[#5c5a55] hover:text-[#1a1a18] transition-colors mb-12"
                >
                    <ArrowLeft size={14} strokeWidth={1.4} />
                    {t("common.backToHome")}
                </Link>

                <h1
                    data-testid="press-title"
                    className="text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[1.02] tracking-tight text-balance"
                    style={{
                        fontFamily:
                            '"Cormorant Garamond", "Times New Roman", serif',
                    }}
                >
                    {t("press.title")}
                </h1>
                <p className="mt-10 max-w-2xl text-[#3d3b36] text-base md:text-[17px] leading-[1.85] font-light">
                    {t("press.intro")}
                </p>

                <ul
                    data-testid="press-articles-grid"
                    className="mt-16 md:mt-24 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
                >
                    {[
                        {
                            publication: "Wallpaper*",
                            title: "Barefoot Luxury villas by Polo Architects in Cabo Verde",
                            date: "Architecture",
                            href: "https://www.wallpaper.com/architecture/barefoot-luxury-villas-polo-architects-cabo-verde",
                            image: "https://customer-assets.emergentagent.com/job_video-page-1/artifacts/83c6g5ga_image.png",
                        },
                        {
                            publication: "Knack Weekend",
                            title: "Belgisch architectenteam creëert vakantieproject in Kaapverdische oase",
                            date: "Lifestyle · Design",
                            href: "https://weekend.knack.be/lifestyle/design/belgisch-architectenteam-creeert-vakantieproject-in-kaapverdische-oase/",
                            image: "/assets/press-knack.jpg",
                        },
                        {
                            publication: "De Tijd · Sabato",
                            title: "Belgen bouwen camouflagevilla's op Kaapverdië",
                            date: "Reizen",
                            href: "https://www.tijd.be/sabato/reizen/belgen-bouwen-camouflagevilla-s-op-kaapverdie/10116278.html",
                            image: "/assets/press-sabato.jpg",
                        },
                        {
                            publication: "Dezeen",
                            title: "Barefoot Luxury hotel, Cape Verde — Polo Architects",
                            date: "June 2019",
                            href: "https://www.dezeen.com/2019/06/29/barefoot-luxury-hotel-cape-verde-polo-architects-going-east/",
                            image: "/assets/press-dezeen.jpg",
                        },
                        {
                            publication: "Elle Decor",
                            title: "Resort Barefoot Luxury, Cape Verde — Polo Architects",
                            date: "Best of",
                            href: "https://www.elledecor.com/it/best-of/a28870988/resort-barefoot-luxury-cape-verde-polo-architects-going-east/",
                            image: "/assets/press-elledecor.jpg",
                        },
                        {
                            publication: "Condé Nast Traveller",
                            title: "Cabo Verde hotel review",
                            date: "Travel",
                            href: "https://www.cntraveller.com/article/cabo-verde-hotel-review",
                            image: "/assets/press-cntraveller.jpg",
                        },
                    ].map((a, i) => (
                        <li key={a.href}>
                            <a
                                href={a.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`press-article-${i}`}
                                className={`group relative block h-full aspect-[4/5] sm:aspect-[3/4] p-6 md:p-7 transition-colors duration-500 overflow-hidden ${
                                    a.image
                                        ? "bg-[#1a1a18]"
                                        : "bg-[#f5ecd9] hover:bg-[#1a1a18]"
                                }`}
                            >
                                {a.image && (
                                    <>
                                        <img
                                            src={a.image}
                                            alt={a.title}
                                            loading="lazy"
                                            className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-95 group-hover:scale-[1.04] transition-all duration-700 ease-out"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a18]/85 via-[#1a1a18]/30 to-transparent" />
                                    </>
                                )}
                                <div className="relative flex h-full flex-col justify-between">
                                    <p className={`text-[10px] md:text-[11px] uppercase tracking-[0.35em] transition-colors duration-500 ${
                                        a.image
                                            ? "text-[#b59565]"
                                            : "text-[#7a4a3a] group-hover:text-[#b59565]"
                                    }`}>
                                        {a.date}
                                    </p>

                                    <div>
                                        <h2
                                            className={`text-[22px] md:text-[26px] lg:text-[30px] leading-[1.15] tracking-tight transition-colors duration-500 ${
                                                a.image
                                                    ? "text-[#f5ecd9]"
                                                    : "text-[#1a1a18] group-hover:text-[#f5ecd9]"
                                            }`}
                                            style={{
                                                fontFamily:
                                                    '"Cormorant Garamond", "Times New Roman", serif',
                                            }}
                                        >
                                            {a.title}
                                        </h2>
                                        <div className="mt-6 md:mt-8 flex items-end justify-between gap-4">
                                            <p className={`text-[12px] md:text-[13px] uppercase tracking-[0.28em] font-medium transition-colors duration-500 ${
                                                a.image
                                                    ? "text-[#b59565]"
                                                    : "text-[#1a1a18] group-hover:text-[#b59565]"
                                            }`}>
                                                {a.publication}
                                            </p>
                                            <span
                                                aria-hidden="true"
                                                className={`inline-flex items-center justify-center w-9 h-9 rounded-full transition-all duration-500 ${
                                                    a.image
                                                        ? "border border-[#b59565] text-[#b59565] group-hover:bg-[#b59565] group-hover:text-[#1a1a18]"
                                                        : "border border-[#1a1a18] text-[#1a1a18] group-hover:bg-[#b59565] group-hover:border-[#b59565] group-hover:text-[#1a1a18]"
                                                }`}
                                            >
                                                <ArrowRight size={14} strokeWidth={1.4} className="transition-transform duration-500 group-hover:translate-x-0.5" />
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </a>
                        </li>
                    ))}
                </ul>
            </div>
        </main></PageChrome>
    );
}

function Gallery() {
    const { t } = useTranslation();
    useSEO("gallery");
    const [lightboxIdx, setLightboxIdx] = useState(null);
    const photos = [
        { src: "/assets/barefoot-villa.jpg", alt: "Barefoot Luxury — villa exterior" },
        { src: "/assets/barefoot-villa-2.jpg", alt: "Barefoot Luxury — villa lifestyle" },
        ...Array.from({ length: 10 }, (_, i) => ({
            src: `/assets/gallery/gallery-${String(i + 1).padStart(2, "0")}.jpg`,
            alt: `Barefoot Luxury — São Vicente · photo ${i + 1}`,
        })),
        { src: "/assets/gallery/gallery-mindelo.jpg", alt: "Mindelo waterfront — São Vicente, Cabo Verde" },
        { src: "/assets/gallery/gallery-baia-evora.jpg", alt: "Baía de João d'Évora — São Vicente" },
        { src: "/assets/gallery/gallery-barefoot-11.jpg", alt: "Barefoot Luxury — terrace lounge with mountain view" },
        { src: "/assets/gallery/gallery-barefoot-0324.jpg", alt: "Barefoot Luxury — stone villa entrance with wooden shutters" },
        { src: "/assets/gallery/gallery-barefoot-0229.jpg", alt: "Barefoot Luxury — pergola deck above the pool" },
        { src: "/assets/gallery/gallery-barefoot-0349.jpg", alt: "Barefoot Luxury — stone villa at sunset" },
        { src: "/assets/gallery/gallery-barefoot-0547.jpg", alt: "Mindelo municipal market — São Vicente" },
        { src: "/assets/gallery/gallery-barefoot-0601.jpg", alt: "Local life — Mindelo street portrait" },
        { src: "/assets/gallery/gallery-barefoot-0650.jpg", alt: "Fisherman preparing the daily catch" },
        { src: "/assets/gallery/gallery-barefoot-0683.jpg", alt: "Mindelo harbour — fishermen on the dock" },
        { src: "/assets/gallery/gallery-barefoot-0992.jpg", alt: "Local market — Mindelo, São Vicente" },
        { src: "/assets/gallery/gallery-barefoot-1293.jpg", alt: "Barefoot Luxury — sea view through floor-to-ceiling window" },
        { src: "/assets/gallery/gallery-barefoot-1336.jpg", alt: "Barefoot Luxury — sunlit lounge with ceramics" },
        { src: "/assets/gallery/gallery-barefoot-3016.jpg", alt: "Mindelo skyline — colourful houses at dusk" },
        { src: "/assets/gallery/gallery-barefoot-3062.jpg", alt: "Watching Mindelo harbour from above" },
        { src: "/assets/gallery/gallery-barefoot-aerial.jpg", alt: "Aerial top-down view of a Barefoot Luxury villa with private pool" },
        { src: "https://customer-assets.emergentagent.com/job_video-page-1/artifacts/83c6g5ga_image.png", alt: "Polo Architects — villas in the desert (Wallpaper*)" },
        { src: "/assets/press-knack.jpg", alt: "Aerial view — Barefoot Luxury (Knack Weekend)" },
        { src: "/assets/press-sabato.jpg", alt: "São Vicente church (De Tijd · Sabato)" },
        { src: "/assets/press-dezeen.jpg", alt: "Villa interior — wood and glass (Dezeen)" },
        { src: "/assets/press-elledecor.jpg", alt: "Stone façade villa (Elle Decor)" },
        { src: "/assets/press-cntraveller.jpg", alt: "Open kitchen and stone wall (Condé Nast Traveller)" },
    ];

    const total = photos.length;
    const closeLightbox = () => setLightboxIdx(null);
    const nextLightbox = () => setLightboxIdx((i) => (i === null ? null : (i + 1) % total));
    const prevLightbox = () => setLightboxIdx((i) => (i === null ? null : (i - 1 + total) % total));

    useEffect(() => {
        if (lightboxIdx === null) return;
        const onKey = (e) => {
            if (e.key === "Escape") closeLightbox();
            else if (e.key === "ArrowRight") nextLightbox();
            else if (e.key === "ArrowLeft") prevLightbox();
        };
        window.addEventListener("keydown", onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [lightboxIdx, total]);

    return (
        <PageChrome><main
            data-testid="gallery-page"
            className="min-h-screen bg-[#fdfbf7] text-[#1a1a18]"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-32 md:pt-44 pb-16 md:pb-20">
                <Link
                    to="/"
                    data-testid="gallery-back-link"
                    className="inline-flex items-center gap-2 text-[11px] md:text-[12px] uppercase tracking-[0.28em] text-[#5c5a55] hover:text-[#1a1a18] transition-colors mb-12"
                >
                    <ArrowLeft size={14} strokeWidth={1.4} />
                    {t("common.backToHome")}
                </Link>

                <h1
                    data-testid="gallery-title"
                    className="text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[1.02] tracking-tight text-balance"
                    style={{
                        fontFamily:
                            '"Cormorant Garamond", "Times New Roman", serif',
                    }}
                >
                    {t("gallery.title")}
                </h1>
                <p className="mt-10 max-w-2xl text-[#3d3b36] text-base md:text-[17px] leading-[1.85] font-light">
                    {t("gallery.intro")}
                </p>
            </div>

            <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pb-24 md:pb-32">
                <div
                    data-testid="gallery-grid"
                    className="columns-1 sm:columns-2 lg:columns-3 gap-4 md:gap-6 [&>*]:mb-4 md:[&>*]:mb-6"
                >
                    {photos.map((p, i) => (
                        <button
                            type="button"
                            key={`${p.src}-${i}`}
                            data-testid={`gallery-photo-${i}`}
                            onClick={() => setLightboxIdx(i)}
                            aria-label={`Open photo ${i + 1} of ${photos.length}`}
                            className="group relative overflow-hidden bg-[#f3ead9] break-inside-avoid w-full block cursor-zoom-in"
                        >
                            <img
                                src={p.src}
                                alt={p.alt}
                                loading={i < 3 ? "eager" : "lazy"}
                                className="w-full h-auto object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]"
                            />
                        </button>
                    ))}
                </div>
            </div>

            {/* Lightbox */}
            {lightboxIdx !== null && (
                <div
                    data-testid="gallery-lightbox"
                    role="dialog"
                    aria-modal="true"
                    aria-label={photos[lightboxIdx].alt}
                    className="fixed inset-0 z-[100] bg-[#0a0a09]/95 backdrop-blur-md flex items-center justify-center animate-in fade-in duration-300"
                    onClick={closeLightbox}
                >
                    <button
                        type="button"
                        data-testid="gallery-lightbox-close"
                        onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
                        aria-label="Close"
                        className="absolute top-4 right-4 md:top-6 md:right-6 z-10 w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-colors"
                    >
                        <X size={22} strokeWidth={1.6} />
                    </button>
                    <button
                        type="button"
                        data-testid="gallery-lightbox-prev"
                        onClick={(e) => { e.stopPropagation(); prevLightbox(); }}
                        aria-label="Previous photo"
                        className="absolute left-3 sm:left-6 md:left-10 top-1/2 -translate-y-1/2 z-10 w-11 h-11 md:w-14 md:h-14 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-colors"
                    >
                        <ArrowLeft size={22} strokeWidth={1.6} />
                    </button>
                    <button
                        type="button"
                        data-testid="gallery-lightbox-next"
                        onClick={(e) => { e.stopPropagation(); nextLightbox(); }}
                        aria-label="Next photo"
                        className="absolute right-3 sm:right-6 md:right-10 top-1/2 -translate-y-1/2 z-10 w-11 h-11 md:w-14 md:h-14 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-colors"
                    >
                        <ArrowRight size={22} strokeWidth={1.6} />
                    </button>
                    <figure
                        className="relative max-w-[92vw] max-h-[88vh] flex flex-col items-center"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <img
                            src={photos[lightboxIdx].src}
                            alt={photos[lightboxIdx].alt}
                            data-testid="gallery-lightbox-image"
                            className="max-w-full max-h-[80vh] w-auto h-auto object-contain shadow-2xl"
                        />
                        <figcaption className="mt-4 text-center text-white/70 text-[11px] md:text-[12px] uppercase tracking-[0.28em]">
                            <span data-testid="gallery-lightbox-counter">{String(lightboxIdx + 1).padStart(2, "0")} / {String(photos.length).padStart(2, "0")}</span>
                        </figcaption>
                    </figure>
                </div>
            )}
        </main></PageChrome>
    );
}

function Contact() {
    const { t, i18n } = useTranslation();
    useSEO("contact");
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        interest: "Craft your dream villa",
        message: "",
    });
    const [status, setStatus] = useState("idle"); // idle | loading | success | error
    const [errorMsg, setErrorMsg] = useState("");

    const onChange = (field) => (e) =>
        setForm((f) => ({ ...f, [field]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !/.+@.+\..+/.test(form.email)) {
            setStatus("error");
            setErrorMsg(t("contact.form.errorValidation"));
            return;
        }
        try {
            setStatus("loading");
            await axios.post(`${API}/contact`, {
                name: form.name.trim(),
                email: form.email.trim(),
                phone: form.phone.trim() || undefined,
                interest: form.interest,
                message: form.message.trim() || undefined,
                language: (i18n.language || "en").slice(0, 2).toLowerCase(),
            });
            trackConversion("contact_submit", {
                language: (i18n.language || "en").slice(0, 2).toLowerCase(),
                interest: form.interest,
            });
            setStatus("success");
            setForm({
                name: "",
                email: "",
                phone: "",
                interest: "Craft your dream villa",
                message: "",
            });
        } catch (err) {
            setStatus("error");
            const detail = err?.response?.data?.detail;
            setErrorMsg(typeof detail === "string" ? detail : t("contact.form.errorGeneric"));
        }
    };

    const inputClass =
        "w-full bg-transparent border-0 border-b border-[#cdbfa3] focus:border-[#1a1a18] outline-none py-3 text-[15px] md:text-base text-[#1a1a18] placeholder-[#9a8f78] font-light transition-colors";
    const labelClass =
        "block text-[10px] md:text-[11px] uppercase tracking-[0.35em] text-[#7a4a3a] mb-3";

    return (
        <PageChrome><main
            data-testid="contact-page"
            className="min-h-screen bg-[#fdfbf7] text-[#1a1a18]"
        >
            <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 pt-32 md:pt-44 pb-24 md:pb-32">
                <Link
                    to="/"
                    data-testid="contact-back-link"
                    className="inline-flex items-center gap-2 text-[11px] md:text-[12px] uppercase tracking-[0.28em] text-[#5c5a55] hover:text-[#1a1a18] transition-colors mb-12"
                >
                    <ArrowLeft size={14} strokeWidth={1.4} />
                    {t("common.backToHome")}
                </Link>

                <div className="grid lg:grid-cols-[1.1fr_1fr] gap-16 lg:gap-24">
                    {/* Left — intro */}
                    <div>
                        <h1
                            data-testid="contact-title"
                            className="text-5xl sm:text-6xl md:text-7xl lg:text-[80px] leading-[1.02] tracking-tight text-balance"
                            style={{
                                fontFamily:
                                    '"Cormorant Garamond", "Times New Roman", serif',
                            }}
                        >
                            {t("contact.title")}
                        </h1>
                        <p className="mt-10 max-w-md text-[#3d3b36] text-base md:text-[17px] leading-[1.85] font-light">
                            {t("contact.intro")}
                        </p>

                        <div className="mt-16 space-y-8 text-[#1a1a18]">
                            <div>
                                <p className="text-[10px] uppercase tracking-[0.35em] text-[#7a4a3a] mb-2">{t("contact.emailLabel")}</p>
                                <a
                                    href="mailto:stef@barefootluxury.world"
                                    data-testid="contact-email-link"
                                    className="text-base md:text-lg font-light hover:text-[#7a4a3a] transition-colors"
                                >
                                    stef@barefootluxury.world
                                </a>
                            </div>
                            <div>
                                <p className="text-[10px] uppercase tracking-[0.35em] text-[#7a4a3a] mb-2">{t("contact.locationLabel")}</p>
                                <p className="text-base md:text-lg font-light leading-[1.7]">
                                    {t("contact.location")}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Right — form */}
                    <form
                        onSubmit={onSubmit}
                        data-testid="contact-form"
                        className="space-y-10"
                        noValidate
                    >
                        <div>
                            <label htmlFor="contact-name" className={labelClass}>
                                {t("contact.form.name")}
                            </label>
                            <input
                                id="contact-name"
                                data-testid="contact-form-name"
                                type="text"
                                value={form.name}
                                onChange={onChange("name")}
                                placeholder={t("contact.form.namePlaceholder")}
                                required
                                className={inputClass}
                            />
                        </div>

                        <div>
                            <label htmlFor="contact-email" className={labelClass}>
                                {t("contact.form.email")}
                            </label>
                            <input
                                id="contact-email"
                                data-testid="contact-form-email"
                                type="email"
                                value={form.email}
                                onChange={onChange("email")}
                                placeholder={t("contact.form.emailPlaceholder")}
                                required
                                className={inputClass}
                            />
                        </div>

                        <div>
                            <label htmlFor="contact-phone" className={labelClass}>
                                {t("contact.form.phone")} <span className="lowercase tracking-normal text-[#9a8f78]">{t("contact.form.phoneOptional")}</span>
                            </label>
                            <input
                                id="contact-phone"
                                data-testid="contact-form-phone"
                                type="tel"
                                value={form.phone}
                                onChange={onChange("phone")}
                                placeholder={t("contact.form.phonePlaceholder")}
                                className={inputClass}
                            />
                        </div>

                        <div>
                            <label htmlFor="contact-interest" className={labelClass}>
                                {t("contact.form.interest")}
                            </label>
                            <div className="relative">
                                <select
                                    id="contact-interest"
                                    data-testid="contact-form-interest"
                                    value={form.interest}
                                    onChange={onChange("interest")}
                                    className={`${inputClass} appearance-none pr-10 cursor-pointer`}
                                >
                                    <option value="Craft your dream villa">{t("contact.form.interestOpts.craft")}</option>
                                    <option value="Turnkey villa for sale">{t("contact.form.interestOpts.turnkey")}</option>
                                    <option value="Book your stay">{t("contact.form.interestOpts.book")}</option>
                                    <option value="Rental program">{t("contact.form.interestOpts.rental")}</option>
                                    <option value="Press / Media">{t("contact.form.interestOpts.press")}</option>
                                    <option value="Other">{t("contact.form.interestOpts.other")}</option>
                                </select>
                                <ChevronDown
                                    size={18}
                                    strokeWidth={1.4}
                                    className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 text-[#7a4a3a]"
                                    aria-hidden="true"
                                />
                            </div>
                        </div>

                        <div>
                            <label htmlFor="contact-message" className={labelClass}>
                                {t("contact.form.message")}
                            </label>
                            <textarea
                                id="contact-message"
                                data-testid="contact-form-message"
                                value={form.message}
                                onChange={onChange("message")}
                                placeholder={t("contact.form.messagePlaceholder")}
                                rows={4}
                                className={`${inputClass} resize-none`}
                            />
                        </div>

                        <div className="pt-2">
                            <button
                                type="submit"
                                data-testid="contact-form-submit"
                                disabled={status === "loading"}
                                className="group inline-flex items-center gap-3 px-7 py-4 bg-[#1a1a18] text-[#f5ecd9] text-[11px] md:text-[12px] uppercase tracking-[0.32em] hover:bg-[#b59565] hover:text-[#1a1a18] transition-colors duration-500 disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {status === "loading" ? t("contact.form.sending") : t("contact.form.submit")}
                                <ArrowRight
                                    size={14}
                                    strokeWidth={1.6}
                                    className="transition-transform duration-500 group-hover:translate-x-1"
                                />
                            </button>

                            {status === "success" && (
                                <p
                                    data-testid="contact-form-success"
                                    className="mt-6 text-[13px] md:text-sm text-[#1a1a18] font-light"
                                >
                                    {t("contact.form.success")}
                                </p>
                            )}
                            {status === "error" && (
                                <p
                                    data-testid="contact-form-error"
                                    className="mt-6 text-[13px] md:text-sm text-[#a14334] font-light"
                                >
                                    {errorMsg || t("contact.form.errorGeneric")}
                                </p>
                            )}
                        </div>
                    </form>
                </div>
            </div>
        </main></PageChrome>
    );
}

function About() {
    const { t } = useTranslation();
    useSEO("about");
    return (
        <PageChrome><main
            data-testid="about-page"
            className="min-h-screen bg-[#fdfbf7] text-[#1a1a18]"
        >
            <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-16 pt-32 md:pt-44 pb-24 md:pb-32">
                <Link
                    to="/"
                    data-testid="about-back-link"
                    className="inline-flex items-center gap-2 text-[11px] md:text-[12px] uppercase tracking-[0.28em] text-[#5c5a55] hover:text-[#1a1a18] transition-colors mb-12"
                >
                    <ArrowLeft size={14} strokeWidth={1.4} />
                    {t("common.backToHome")}
                </Link>

                {/* Who are we */}
                <h1
                    data-testid="about-title"
                    className="text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[1.02] tracking-tight text-balance"
                    style={{
                        fontFamily:
                            '"Cormorant Garamond", "Times New Roman", serif',
                    }}
                >
                    {t("about.title")}
                </h1>

                {/* Hero photo — window view */}
                <figure className="mt-12 md:mt-16 overflow-hidden">
                    <img
                        src="/assets/gallery/gallery-barefoot-1336.jpg"
                        alt="Villa to rent Cabo Verde — sea and mountain view through the villa's floor-to-ceiling window"
                        data-testid="about-photo-1"
                        loading="lazy"
                        className="w-full h-[55vh] md:h-[70vh] object-cover"
                    />
                </figure>

                <div
                    data-testid="about-intro"
                    className="mt-14 md:mt-20 max-w-3xl space-y-7 text-[#3d3b36] text-base md:text-[17px] leading-[1.85] font-light"
                >
                    <p>{t("about.p1")}</p>
                    <p>
                        {t("about.p2Prefix")}{" "}
                        <a
                            href="https://www.lifeworld.be"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="about-lifeworld-link"
                            className="text-[#1a1a18] underline underline-offset-[6px] decoration-[#b59565] hover:decoration-[#1a1a18] transition-colors"
                        >
                            www.lifeworld.be
                        </a>
                        {t("about.p2Suffix")}
                    </p>
                    <p>{t("about.p3")}</p>
                </div>

                {/* Photo between intro and partners */}
                <figure className="mt-24 md:mt-32 overflow-hidden">
                    <img
                        src="/assets/gallery/gallery-barefoot-1293.jpg"
                        alt="Sunlit interior of the villa with ceramics and tall concrete walls"
                        data-testid="about-photo-2"
                        loading="lazy"
                        className="w-full h-[55vh] md:h-[70vh] object-cover"
                    />
                </figure>

                {/* Meet our partners */}
                <div className="mt-24 md:mt-36">
                    <p className="text-[12px] md:text-[14px] uppercase tracking-[0.35em] text-[#7a4a3a] mb-6">
                        {t("about.partnersKicker")}
                    </p>
                    <h2
                        data-testid="about-partners-title"
                        className="text-4xl sm:text-5xl md:text-6xl lg:text-[72px] leading-[1.05] tracking-tight text-balance"
                        style={{
                            fontFamily:
                                '"Cormorant Garamond", "Times New Roman", serif',
                        }}
                    >
                        {t("about.partnersTitle")}
                    </h2>

                    <div className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16">
                        {/* Polo Architects */}
                        <article data-testid="about-polo">
                            <h3
                                className="text-3xl md:text-4xl lg:text-[44px] leading-[1.1] tracking-tight"
                                style={{
                                    fontFamily:
                                        '"Cormorant Garamond", "Times New Roman", serif',
                                }}
                            >
                                {t("about.polo.title")}
                            </h3>
                            <div className="mt-5 h-px w-12 bg-[#b59565]" />
                            <p className="mt-7 text-[#3d3b36] text-base md:text-[17px] leading-[1.85] font-light">
                                {t("about.polo.prefix")}{" "}
                                <a
                                    href="https://www.polo-architects.be"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="about-polo-link"
                                    className="text-[#1a1a18] underline underline-offset-[6px] decoration-[#b59565] hover:decoration-[#1a1a18] transition-colors"
                                >
                                    www.polo-architects.be
                                </a>
                                {t("about.polo.suffix")}
                            </p>
                        </article>

                        {/* Stef & Solano */}
                        <article data-testid="about-stef-solano">
                            <h3
                                className="text-3xl md:text-4xl lg:text-[44px] leading-[1.1] tracking-tight"
                                style={{
                                    fontFamily:
                                        '"Cormorant Garamond", "Times New Roman", serif',
                                }}
                            >
                                {t("about.stefSolano.title")}
                            </h3>
                            <div className="mt-5 h-px w-12 bg-[#b59565]" />
                            <p className="mt-7 text-[#3d3b36] text-base md:text-[17px] leading-[1.85] font-light">
                                {t("about.stefSolano.prefix")}{" "}
                                <Link
                                    to="/contact"
                                    data-testid="about-contact-link"
                                    className="text-[#1a1a18] underline underline-offset-[6px] decoration-[#b59565] hover:decoration-[#1a1a18] transition-colors"
                                >
                                    {t("about.stefSolano.linkLabel")}
                                </Link>
                                {t("about.stefSolano.suffix")}
                            </p>
                        </article>
                    </div>
                </div>

                {/* Closing photo — stone villa exterior */}
                <figure className="mt-24 md:mt-36 overflow-hidden">
                    <img
                        src="/assets/gallery/gallery-barefoot-0229.jpg"
                        alt="Stone villa exterior with wooden pivot doors among the rocks"
                        data-testid="about-photo-3"
                        loading="lazy"
                        className="w-full h-[55vh] md:h-[70vh] object-cover"
                    />
                </figure>
            </div>
        </main></PageChrome>
    );
}

function Destination() {
    const { t } = useTranslation();
    useSEO("destination");
    const buildSection = (id, bodyCount) => ({
        id,
        title: t(`destination.sections.${id}.title`),
        heading: t(`destination.sections.${id}.heading`),
        body: Array.from({ length: bodyCount }, (_, i) =>
            t(`destination.sections.${id}.p${i + 1}`),
        ),
    });
    const sections = [
        buildSection("geo", 3),
        buildSection("population", 5),
        buildSection("politics", 3),
        buildSection("climate", 3),
        buildSection("tourism", 8),
    ];

    const serifStyle = {
        fontFamily: '"Cormorant Garamond", "Times New Roman", serif',
    };

    return (
        <PageChrome><main
            data-testid="destination-page"
            className="min-h-screen bg-[#fdfbf7] text-[#1a1a18]"
        >
            {/* Top banner — Mindelo waterfront */}
            <figure
                data-testid="destination-top-banner"
                className="w-full overflow-hidden"
            >
                <img
                    src="/assets/gallery/gallery-mindelo.jpg"
                    alt="Mindelo waterfront — São Vicente"
                    loading="eager"
                    className="w-full h-[55vh] md:h-[75vh] object-cover"
                />
            </figure>

            <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 pt-20 md:pt-28 pb-24 md:pb-32">
                <Link
                    to="/"
                    data-testid="destination-back-link"
                    className="inline-flex items-center gap-2 text-[11px] md:text-[12px] uppercase tracking-[0.28em] text-[#5c5a55] hover:text-[#1a1a18] transition-colors mb-12"
                >
                    <ArrowLeft size={14} strokeWidth={1.4} />
                    {t("common.backToHome")}
                </Link>

                <h1
                    data-testid="destination-title"
                    className="text-4xl sm:text-5xl md:text-6xl lg:text-[80px] leading-[1.02] tracking-tight text-balance"
                    style={serifStyle}
                >
                    {t("destination.title")}
                </h1>

                <p
                    data-testid="destination-intro"
                    className="mt-12 md:mt-16 max-w-3xl text-[#3d3b36] text-base md:text-[17px] leading-[1.85] font-light"
                >
                    <Trans i18nKey="destination.intro" components={{ i: <em /> }} />
                </p>

                {/* World map */}
                <figure className="mt-16 md:mt-24 bg-[#fdfbf7]">
                    <img
                        src="/assets/maps/world.png"
                        alt="World map showing connections from Brussels, Amsterdam, Paris, Lisboa, New York, Dakar, Luanda, Rio de Janeiro and Paramaribo to Cabo Verde"
                        data-testid="destination-world-map"
                        loading="lazy"
                        className="w-full h-auto"
                    />
                </figure>

                {/* Cabo Verde */}
                <div className="mt-24 md:mt-32 max-w-3xl">
                    <p className="text-[12px] md:text-[14px] uppercase tracking-[0.35em] text-[#7a4a3a] mb-6">
                        {t("destination.archipelagoKicker")}
                    </p>
                    <h2
                        className="text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight"
                        style={serifStyle}
                    >
                        {t("destination.archipelagoTitle")}
                    </h2>
                    <p className="mt-8 text-[#3d3b36] text-base md:text-[17px] leading-[1.85] font-light">
                        {t("destination.archipelagoText")}
                    </p>
                </div>

                <figure className="mt-12 md:mt-16">
                    <img
                        src="/assets/maps/archipelago.png"
                        alt="Cabo Verde archipelago map with flight and ferry times between islands"
                        data-testid="destination-archipelago-map"
                        loading="lazy"
                        className="w-full h-auto max-w-3xl mx-auto"
                    />
                </figure>

                {/* São Vicente */}
                <div className="mt-24 md:mt-32 max-w-3xl">
                    <p className="text-[12px] md:text-[14px] uppercase tracking-[0.35em] text-[#7a4a3a] mb-6">
                        {t("destination.islandKicker")}
                    </p>
                    <h2
                        className="text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight"
                        style={serifStyle}
                    >
                        {t("destination.islandTitle")}
                    </h2>
                    <p className="mt-8 text-[#3d3b36] text-base md:text-[17px] leading-[1.85] font-light">
                        {t("destination.islandText")}
                    </p>
                </div>

                <figure className="mt-12 md:mt-16">
                    <img
                        src="/assets/maps/island.png"
                        alt="Map of São Vicente island — 8 min from Mindelo, 20 min from São Pedro airport"
                        data-testid="destination-island-map"
                        loading="lazy"
                        className="w-full h-auto max-w-md md:max-w-lg mx-auto"
                    />
                </figure>

                {/* Accordion */}
                <div className="mt-24 md:mt-36">
                    <p className="text-[12px] md:text-[14px] uppercase tracking-[0.35em] text-[#7a4a3a] mb-6">
                        {t("destination.inDepthKicker")}
                    </p>
                    <h2
                        className="text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight mb-10 md:mb-14"
                        style={serifStyle}
                    >
                        {t("destination.inDepthTitle")}
                    </h2>

                    <Accordion
                        type="single"
                        collapsible
                        data-testid="destination-accordion"
                        className="border-t border-[#1a1a18]/15"
                    >
                        {sections.map((s) => (
                            <AccordionItem
                                key={s.id}
                                value={s.id}
                                className="border-b border-[#1a1a18]/15"
                            >
                                <AccordionTrigger
                                    data-testid={`destination-acc-${s.id}`}
                                    className="py-6 md:py-8 text-left text-[15px] md:text-[18px] tracking-wide font-light text-[#1a1a18] hover:no-underline hover:text-[#7a4a3a] [&[data-state=open]>svg]:text-[#7a4a3a]"
                                    style={serifStyle}
                                >
                                    <span className="pr-6">{s.title}</span>
                                </AccordionTrigger>
                                <AccordionContent className="pb-10 md:pb-14 max-w-3xl">
                                    <p
                                        className="text-[15px] md:text-[18px] uppercase tracking-[0.18em] leading-[1.6] text-[#1a1a18] font-medium"
                                    >
                                        {s.heading}
                                    </p>
                                    <div className="mt-6 space-y-5 text-[#3d3b36] text-base md:text-[16px] leading-[1.85] font-light normal-case tracking-normal">
                                        {s.body.map((p, idx) => (
                                            <p key={idx}>{p}</p>
                                        ))}
                                    </div>
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </div>

            {/* Bottom banner — man on the bench */}
            <figure
                data-testid="destination-bottom-banner"
                className="w-full overflow-hidden"
            >
                <img
                    src="/assets/gallery/gallery-barefoot-3016.jpg"
                    alt="Mindelo harbour — a local man contemplating the bay"
                    loading="lazy"
                    className="w-full h-[55vh] md:h-[75vh] object-cover"
                />
            </figure>
        </main></PageChrome>
    );
}

function TurnkeyVilla() {
    const { t } = useTranslation();
    useSEO("turnkey");
    const serifStyle = {
        fontFamily: '"Cormorant Garamond", "Times New Roman", serif',
    };

    const villas = [
        {
            number: "Villa 24",
            type: "Type 2",
            price: "895.000 €",
            note: "plot included",
            modelVilla: true,
            image: "/assets/villa-24.jpg",
            status: null,
            href: "/turnkey-villa/villa-24",
        },
        {
            number: "Villa 25",
            type: "Type 3",
            price: "855.000 €",
            note: "plot included",
            image: "/assets/gallery/gallery-barefoot-0229.jpg",
            status: "sold",
        },
        {
            number: "Villa 26",
            type: "Type 3",
            price: "825.000 €",
            note: "plot included",
            image: "/assets/villa-26.jpg",
            status: "sold",
        },
    ];

    return (
        <PageChrome><main
            data-testid="turnkey-page"
            className="min-h-screen bg-[#fdfbf7] text-[#1a1a18]"
        >
            {/* Top banner */}
            <figure
                data-testid="turnkey-top-banner"
                className="w-full overflow-hidden"
            >
                <img
                    src="/assets/turnkey/devotal-banner.jpg"
                    alt="Devotal by Bob Lima — Barefoot Luxury"
                    loading="eager"
                    className="w-full h-[55vh] md:h-[75vh] object-cover"
                />
            </figure>

            <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 pt-20 md:pt-28 pb-24 md:pb-32">
                <Link
                    to="/"
                    data-testid="turnkey-back-link"
                    className="inline-flex items-center gap-2 text-[11px] md:text-[12px] uppercase tracking-[0.28em] text-[#5c5a55] hover:text-[#1a1a18] transition-colors mb-12"
                >
                    <ArrowLeft size={14} strokeWidth={1.4} />
                    {t("common.backToHome")}
                </Link>

                <h1
                    data-testid="turnkey-title"
                    className="text-4xl sm:text-5xl md:text-6xl lg:text-[72px] leading-[1.05] tracking-tight text-balance"
                    style={serifStyle}
                >
                    {t("turnkey.title")}
                </h1>

                <div
                    data-testid="turnkey-intro"
                    className="mt-12 md:mt-16 max-w-3xl space-y-7 text-[#3d3b36] text-base md:text-[17px] leading-[1.85] font-light"
                >
                    <p>{t("turnkey.p1")}</p>
                    <p>{t("turnkey.p2")}</p>
                    <p>{t("turnkey.p3")}</p>
                </div>
            </div>

            {/* 3 villa cards */}
            <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pb-24 md:pb-36">
                <ul
                    data-testid="turnkey-villas-grid"
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10 lg:gap-14"
                >
                    {villas.map((v, i) => (
                        <li
                            key={v.number}
                            data-testid={`turnkey-villa-card-${i}`}
                            className="group"
                        >
                            {/* Image with label */}
                            {v.href ? (
                                <Link to={v.href} data-testid={`turnkey-villa-image-link-${i}`} className="block relative aspect-[3/4] overflow-hidden bg-[#f3ead9]">
                                    <img
                                        src={v.image}
                                        alt={v.number}
                                        loading="lazy"
                                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
                                    />
                                    <span
                                        className="absolute top-4 left-4 md:top-5 md:left-5 px-3.5 py-1.5 bg-[#f3ead9] text-[#1a1a18] text-[10px] md:text-[11px] uppercase tracking-[0.28em] font-medium"
                                        style={serifStyle}
                                    >
                                        {t("turnkey.finishedVilla")}
                                    </span>
                                </Link>
                            ) : (
                                <div className="relative aspect-[3/4] overflow-hidden bg-[#f3ead9]">
                                    <img
                                        src={v.image}
                                        alt={v.number}
                                        loading="lazy"
                                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
                                    />
                                    <span
                                        className="absolute top-4 left-4 md:top-5 md:left-5 px-3.5 py-1.5 bg-[#f3ead9] text-[#1a1a18] text-[10px] md:text-[11px] uppercase tracking-[0.28em] font-medium"
                                        style={serifStyle}
                                    >
                                        {t("turnkey.finishedVilla")}
                                    </span>
                                    {v.status === "sold" && (
                                        <>
                                            <span
                                                data-testid={`turnkey-villa-sold-badge-${i}`}
                                                className="absolute inset-0 flex items-center justify-center pointer-events-none z-10"
                                            >
                                                <span
                                                    className="px-6 md:px-8 py-2 md:py-3 bg-[#c94a3d] text-white text-xl md:text-3xl uppercase tracking-[0.32em] font-medium shadow-[0_8px_30px_rgba(0,0,0,0.35)]"
                                                    style={serifStyle}
                                                >
                                                    {t("common.sold")}
                                                </span>
                                            </span>
                                            <div className="absolute inset-0 bg-[#1a1a18]/25 pointer-events-none" />
                                        </>
                                    )}
                                </div>
                            )}

                            {/* Caption */}
                            <div className="mt-6 md:mt-8 text-center">
                                <h3
                                    className="text-3xl md:text-4xl leading-none tracking-tight text-[#3a6a68]"
                                    style={serifStyle}
                                >
                                    {v.number}
                                </h3>
                                <p className="mt-1 text-[13px] md:text-[14px] uppercase tracking-[0.28em] text-[#5c8583] font-light">
                                    ({v.type})
                                </p>
                                {v.modelVilla && (
                                    <p
                                        data-testid={`turnkey-villa-model-${i}`}
                                        className="mt-3 text-[13px] md:text-[15px] italic text-[#7a4a3a] font-light"
                                        style={serifStyle}
                                    >
                                        {t("turnkey.ourModelVilla")}
                                    </p>
                                )}
                                {v.status !== "sold" && (
                                    <>
                                        <p
                                            className="mt-5 text-[18px] md:text-[20px] text-[#1a1a18]"
                                            style={serifStyle}
                                        >
                                            {v.price}
                                        </p>
                                        <p className="mt-1 text-[12px] md:text-[13px] text-[#5c5a55] italic font-light">
                                            ({t("turnkey.plotIncluded")})
                                        </p>
                                    </>
                                )}

                                {v.status === "sold" ? (
                                    <span
                                        data-testid={`turnkey-villa-sold-${i}`}
                                        className="mt-7 inline-flex items-center gap-2 text-[10px] md:text-[11px] uppercase tracking-[0.32em] text-[#c94a3d] border-b border-[#c94a3d] pb-1 select-none"
                                    >
                                        {t("common.sold")}
                                    </span>
                                ) : (
                                    <Link
                                        to={v.href || "/contact"}
                                        data-testid={`turnkey-villa-cta-${i}`}
                                        className="mt-7 inline-flex items-center gap-2 text-[10px] md:text-[11px] uppercase tracking-[0.32em] text-[#1a1a18] border-b border-[#1a1a18] pb-1 hover:text-[#7a4a3a] hover:border-[#7a4a3a] transition-colors"
                                    >
                                        {v.href ? t("common.discover") : t("common.enquire")}
                                        <ArrowRight size={12} strokeWidth={1.6} />
                                    </Link>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
        </main></PageChrome>
    );
}

const VILLA_TYPES = [
    { n: 1, price: "731.000 €", image: "/assets/villa-types/type-1.jpg", href: "/villas-for-sale/type-1" },
    { n: 2, price: "713.000 €", image: "/assets/villa-types/type-2.jpg", href: "/villas-for-sale/type-2" },
    { n: 3, price: "739.000 €", image: "/assets/villa-types/type-3.jpg", href: "/villas-for-sale/type-3" },
    { n: 4, price: "668.000 €", image: "/assets/villa-types/type-4.jpg", href: "/villas-for-sale/type-4" },
    { n: 5, price: "555.000 €", image: "/assets/villa-types/type-5.jpg", href: "/villas-for-sale/type-5" },
];

function VillasForSale() {
    useSEO("villasForSale");
    const { t } = useTranslation();
    const serifStyle = {
        fontFamily: '"Cormorant Garamond", "Times New Roman", serif',
    };

    const features = ["windows", "kitchen", "bedrooms", "bathrooms", "terraces", "pool", "finish"];

    return (
        <PageChrome>
            <main
                data-testid="villas-for-sale-page"
                className="min-h-screen bg-[#fdfbf7] text-[#1a1a18]"
            >
                {/* Full-width video */}
                <section
                    data-testid="villas-for-sale-video"
                    className="relative w-full aspect-[21/9] md:aspect-[21/8] overflow-hidden bg-[#1a1a18]"
                >
                    <video
                        autoPlay
                        muted
                        loop
                        playsInline
                        {...{ "webkit-playsinline": "true", "x5-playsinline": "true", "x5-video-player-type": "h5", "x5-video-player-fullscreen": "false" }}
                        controls={false}
                        disablePictureInPicture
                        disableRemotePlayback
                        preload="metadata"
                        className="absolute inset-0 w-full h-full object-cover"
                        ref={(el) => {
                            if (!el) return;
                            el.muted = true;
                            const p = el.play();
                            if (p && typeof p.catch === "function") p.catch(() => {});
                        }}
                    >
                        <source src="/assets/villa-types/hero-pool-mobile.mp4" type="video/mp4" media="(max-width: 767px)" />
                        <source src="/assets/villa-types/hero-pool.mp4" type="video/mp4" />
                    </video>
                </section>

                {/* Hero */}
                <section className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 pt-20 md:pt-28 pb-14 md:pb-20 text-center">
                    <p className="text-[11px] md:text-[12px] uppercase tracking-[0.4em] text-[#7a4a3a] mb-6">
                        {t("villasForSale.kicker")}
                    </p>
                    <h1
                        data-testid="villas-for-sale-title"
                        className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[#1a1a18]"
                        style={serifStyle}
                    >
                        {t("villasForSale.heading")}
                    </h1>
                    <p className="mt-10 md:mt-12 max-w-3xl mx-auto text-[#3d3b36] text-base md:text-[17px] leading-[1.85] font-light">
                        {t("villasForSale.intro")}
                    </p>
                </section>

                {/* 5 Villa types */}
                <section
                    data-testid="villa-types-grid"
                    className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pb-24 md:pb-32"
                >
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10 md:mb-14">
                        <p className="text-[11px] md:text-[12px] uppercase tracking-[0.35em] text-[#7a4a3a]">
                            {t("villasForSale.typesKicker")}
                        </p>
                        <h2
                            className="text-2xl md:text-3xl text-[#1a1a18]"
                            style={serifStyle}
                        >
                            {t("villasForSale.typesHeading")}
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
                        {VILLA_TYPES.map((v) => {
                            const CardWrapper = v.href ? Link : "div";
                            const wrapperProps = v.href ? { to: v.href } : {};
                            return (
                                <article
                                    key={v.n}
                                    data-testid={`villa-type-card-${v.n}`}
                                    className="group flex flex-col"
                                >
                                    <CardWrapper
                                        {...wrapperProps}
                                        data-testid={v.href ? `villa-type-image-link-${v.n}` : undefined}
                                        className="relative aspect-[4/3] overflow-hidden bg-[#f3ead9] block"
                                    >
                                        <img
                                            src={v.image}
                                            alt={t("villasForSale.typeLabel", { n: v.n })}
                                            loading="lazy"
                                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
                                        />
                                        <span
                                            className="absolute top-4 left-4 md:top-5 md:left-5 px-3.5 py-1.5 bg-[#f3ead9] text-[#1a1a18] text-[10px] md:text-[11px] uppercase tracking-[0.28em] font-medium"
                                            style={serifStyle}
                                        >
                                            {t("villasForSale.typeLabel", { n: v.n })}
                                        </span>
                                    </CardWrapper>
                                    <div className="mt-6 flex items-baseline justify-between gap-4">
                                        <div>
                                            <p className="text-[10px] uppercase tracking-[0.32em] text-[#7a4a3a] mb-1">
                                                {t("villasForSale.priceFrom")}
                                            </p>
                                            <p
                                                className="text-3xl md:text-[34px] text-[#1a1a18] leading-none"
                                                style={serifStyle}
                                            >
                                                {v.price}
                                            </p>
                                            <p className="mt-2 text-[12px] text-[#5c5a55] italic font-light">
                                                {t("villasForSale.plotNotIncluded")}
                                            </p>
                                            {v.href && (
                                                <Link
                                                    to={v.href}
                                                    data-testid={`villa-type-discover-${v.n}`}
                                                    className="mt-5 inline-flex items-center gap-2 text-[10px] md:text-[11px] uppercase tracking-[0.32em] text-[#1a1a18] border-b border-[#1a1a18] pb-1 hover:text-[#7a4a3a] hover:border-[#7a4a3a] transition-colors"
                                                >
                                                    {t("common.discover")}
                                                    <ArrowRight size={12} strokeWidth={1.6} />
                                                </Link>
                                            )}
                                        </div>
                                    </div>
                                </article>
                            );
                        })}

                        {/* Customize your villa — 6th card */}
                        <article
                            data-testid="villa-customize-card"
                            className="group flex flex-col"
                        >
                            <div className="relative aspect-[4/3] overflow-hidden bg-[#f3ead9]">
                                <img
                                    src="/assets/villa-types/customize.jpg"
                                    alt={t("villasForSale.customizeTitle")}
                                    loading="lazy"
                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
                                />
                                <span
                                    className="absolute top-4 left-4 md:top-5 md:left-5 px-3.5 py-1.5 bg-[#f3ead9] text-[#1a1a18] text-[10px] md:text-[11px] uppercase tracking-[0.28em] font-medium"
                                    style={serifStyle}
                                >
                                    {t("villasForSale.customizeTitle")}
                                </span>
                            </div>
                            <div className="mt-6">
                                <p className="text-[10px] uppercase tracking-[0.32em] text-[#7a4a3a] mb-1">
                                    {t("villasForSale.customizeInfo")}
                                </p>
                                <p
                                    className="text-3xl md:text-[34px] text-[#1a1a18] leading-none"
                                    style={serifStyle}
                                >
                                    {t("villasForSale.customizePrice")}
                                </p>
                            </div>
                        </article>
                    </div>
                </section>

                {/* Plots available banner */}
                <section
                    data-testid="plots-available-section"
                    className="bg-[#f3ead9]"
                >
                    <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28 text-center">
                        <h2
                            className="text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight text-[#1a1a18]"
                            style={serifStyle}
                        >
                            {t("villasForSale.plotsHeading")}
                        </h2>
                        <p
                            className="mt-6 text-5xl md:text-6xl lg:text-7xl text-[#7a4a3a] leading-none"
                            style={serifStyle}
                        >
                            {t("villasForSale.plotsPrice")}
                        </p>
                        <p className="mt-8 max-w-2xl mx-auto text-[#3d3b36] text-base md:text-[17px] leading-[1.85] font-light">
                            {t("villasForSale.plotsDesc")}
                        </p>
                    </div>
                </section>

                {/* Features */}
                <section
                    data-testid="villa-features-section"
                    className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28"
                >
                    <p className="text-[11px] md:text-[12px] uppercase tracking-[0.4em] text-[#7a4a3a] mb-12 text-center">
                        {t("villasForSale.featuresKicker")}
                    </p>
                    <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-x-6 gap-y-12 md:gap-y-14">
                        {features.map((key, i) => (
                            <li
                                key={key}
                                data-testid={`villa-feature-${i}`}
                                className="flex flex-col items-center text-center"
                            >
                                <img
                                    src={`/assets/villa-24/icons/${key}.svg`}
                                    alt=""
                                    aria-hidden="true"
                                    className="h-16 md:h-20 w-auto mb-5 select-none"
                                    draggable="false"
                                />
                                <p className="text-[12px] md:text-[13px] leading-[1.5] text-[#1a1a18] font-light lowercase">
                                    {t(`villasForSale.features.${key}`)}
                                </p>
                            </li>
                        ))}
                    </ul>
                </section>

                {/* Bottom CTA */}
                <section className="max-w-3xl mx-auto px-6 md:px-12 lg:px-16 pt-6 pb-4 text-center">
                    <h2
                        className="text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight text-[#1a1a18]"
                        style={serifStyle}
                    >
                        {t("villasForSale.ctaHeading")}
                    </h2>
                    <p className="mt-6 text-[#3d3b36] text-base md:text-[17px] leading-[1.85] font-light">
                        {t("villasForSale.ctaDesc")}
                    </p>
                </section>
            </main>
        </PageChrome>
    );
}



function HotelRentalProgram() {
    useSEO("hotelRental");
    const { t } = useTranslation();
    const serifStyle = {
        fontFamily: '"Cormorant Garamond", "Times New Roman", serif',
    };

    const steps = t("hotelRental.processSteps", { returnObjects: true });
    const ownerItems = t("hotelRental.ownerItems", { returnObjects: true });
    const guestItems = t("hotelRental.guestItems", { returnObjects: true });
    const includedItems = t("hotelRental.includedItems", { returnObjects: true });
    const models = t("hotelRental.models", { returnObjects: true });

    return (
        <PageChrome>
            <main
                data-testid="hotel-rental-page"
                className="min-h-screen bg-[#fdfbf7] text-[#1a1a18]"
            >
                {/* Hero */}
                <section className="relative">
                    <div className="relative w-full aspect-[4/5] sm:aspect-[16/10] md:aspect-[21/9] overflow-hidden bg-[#f3ead9]">
                        <img
                            src="/assets/hotel-rental/hero-pool.webp"
                        alt="Villa rental São Vicente with hotelier services — private pool of a Barefoot Luxury villa in Cabo Verde"
                            className="absolute inset-0 w-full h-full object-cover object-center"
                        />
                        <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a18]/10 via-[#1a1a18]/20 to-[#1a1a18]/80 pointer-events-none" />
                        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 lg:p-20">
                            <p
                                className="text-[11px] md:text-[12px] uppercase tracking-[0.4em] text-[#f5ecd9] mb-4 md:mb-6"
                                style={{ textShadow: "0 2px 12px rgba(0,0,0,0.65)" }}
                            >
                                {t("hotelRental.kicker")}
                            </p>
                            <h1
                                data-testid="hotel-rental-title"
                                className="text-white text-4xl sm:text-5xl md:text-6xl lg:text-[80px] leading-[1.02] tracking-tight max-w-4xl"
                                style={{ ...serifStyle, textShadow: "0 4px 24px rgba(0,0,0,0.45)" }}
                            >
                                {t("hotelRental.heroTitle")}
                            </h1>
                            <p
                                className="mt-6 md:mt-8 max-w-2xl text-[#f5ecd9] text-base md:text-[17px] leading-[1.85] font-light"
                                style={{ textShadow: "0 2px 12px rgba(0,0,0,0.55)" }}
                            >
                                {t("hotelRental.heroLede")}
                            </p>
                        </div>
                    </div>
                </section>

                {/* Concept */}
                <section
                    data-testid="hotel-rental-concept"
                    className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 pt-20 md:pt-32 pb-20 md:pb-28"
                >
                    <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-10 lg:gap-20 items-start">
                        <div>
                            <h2
                                className="text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight text-[#1a1a18]"
                                style={serifStyle}
                            >
                                {t("hotelRental.conceptTitle")}
                            </h2>

                            {/* Occupancy stat pastille */}
                            <div
                                data-testid="hotel-rental-occupancy-badge"
                                className="mt-10 md:mt-14 flex items-center gap-6"
                            >
                                <div
                                    aria-hidden="true"
                                    className="relative flex-shrink-0 w-32 h-32 md:w-40 md:h-40 rounded-full bg-[#f3ead9] flex flex-col items-center justify-center shadow-[0_10px_40px_rgba(181,149,101,0.25)] ring-1 ring-[#b59565]/25"
                                >
                                    <span
                                        className="text-5xl md:text-6xl text-[#7a4a3a] leading-none"
                                        style={serifStyle}
                                    >
                                        {t("hotelRental.occupancyStat")}
                                    </span>
                                    <span className="mt-1 text-[10px] uppercase tracking-[0.3em] text-[#7a4a3a]/80">
                                        occupancy
                                    </span>
                                </div>
                                <p className="max-w-xs text-[#3d3b36] text-sm md:text-[15px] leading-[1.6] font-light italic">
                                    {t("hotelRental.occupancyLabel")}
                                </p>
                            </div>
                        </div>
                        <div className="space-y-6 text-[#3d3b36] text-base md:text-[17px] leading-[1.85] font-light">
                            <p>{t("hotelRental.conceptP1")}</p>
                            <p>{t("hotelRental.conceptP2")}</p>
                            <p>{t("hotelRental.conceptP3")}</p>
                        </div>
                    </div>
                </section>

                {/* Process — 5 steps */}
                <section
                    data-testid="hotel-rental-process"
                    className="bg-[#f3ead9]/60 py-20 md:py-28"
                >
                    <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16">
                        <p className="text-[12px] md:text-[14px] uppercase tracking-[0.35em] text-[#7a4a3a] mb-6">
                            {t("hotelRental.processKicker")}
                        </p>
                        <h2
                            className="text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight text-[#1a1a18] max-w-3xl mb-14 md:mb-20"
                            style={serifStyle}
                        >
                            {t("hotelRental.processTitle")}
                        </h2>

                        <ol className="space-y-10 md:space-y-14">
                            {steps.map((s, i) => (
                                <li
                                    key={s.num}
                                    data-testid={`hotel-rental-step-${i}`}
                                    className="grid grid-cols-[auto_1fr] md:grid-cols-[120px_1fr_2fr] gap-6 md:gap-10 items-start border-t border-[#1a1a18]/15 pt-10 md:pt-12"
                                >
                                    <span
                                        className="text-4xl md:text-5xl lg:text-6xl text-[#7a4a3a] leading-none"
                                        style={serifStyle}
                                    >
                                        {s.num}
                                    </span>
                                    <h3
                                        className="text-xl md:text-2xl lg:text-3xl text-[#1a1a18] leading-tight col-span-1 md:col-span-1"
                                        style={serifStyle}
                                    >
                                        {s.title}
                                    </h3>
                                    <p className="col-span-2 md:col-span-1 text-[#3d3b36] text-base md:text-[16px] leading-[1.85] font-light">
                                        {s.body}
                                    </p>
                                </li>
                            ))}
                        </ol>
                    </div>
                </section>

                {/* Benefits — Owner vs Guests */}
                <section
                    data-testid="hotel-rental-benefits"
                    className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 pt-20 md:pt-28 pb-8 md:pb-12"
                >
                    <p className="text-[12px] md:text-[14px] uppercase tracking-[0.35em] text-[#7a4a3a] mb-6">
                        {t("hotelRental.benefitsKicker")}
                    </p>
                    <h2
                        className="text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight text-[#1a1a18] max-w-3xl mb-14 md:mb-20"
                        style={serifStyle}
                    >
                        {t("hotelRental.benefitsTitle")}
                    </h2>

                    <div className="grid md:grid-cols-2 gap-10 md:gap-16">
                        <div>
                            <p className="text-[11px] md:text-[12px] uppercase tracking-[0.32em] text-[#7a4a3a] pb-4 border-b border-[#1a1a18]/15 mb-6">
                                {t("hotelRental.ownerHeading")}
                            </p>
                            <ul className="space-y-4">
                                {ownerItems.map((item, i) => (
                                    <li
                                        key={i}
                                        data-testid={`hotel-rental-owner-item-${i}`}
                                        className="flex gap-3 text-[#3d3b36] text-base md:text-[16px] leading-[1.65] font-light"
                                    >
                                        <span className="mt-3 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-[#7a4a3a]" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div>
                            <p className="text-[11px] md:text-[12px] uppercase tracking-[0.32em] text-[#7a4a3a] pb-4 border-b border-[#1a1a18]/15 mb-6">
                                {t("hotelRental.guestHeading")}
                            </p>
                            <ul className="space-y-4">
                                {guestItems.map((item, i) => (
                                    <li
                                        key={i}
                                        data-testid={`hotel-rental-guest-item-${i}`}
                                        className="flex gap-3 text-[#3d3b36] text-base md:text-[16px] leading-[1.65] font-light"
                                    >
                                        <span className="mt-3 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-[#7a4a3a]" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </section>

                {/* Philosophy quote */}
                <section
                    data-testid="hotel-rental-philosophy"
                    className="max-w-4xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28 text-center"
                >
                    <p className="text-[11px] md:text-[12px] uppercase tracking-[0.4em] text-[#7a4a3a] mb-8">
                        {t("hotelRental.philosophyKicker")}
                    </p>
                    <blockquote
                        className="text-2xl sm:text-3xl md:text-4xl lg:text-[42px] leading-[1.3] tracking-tight text-[#1a1a18] italic"
                        style={serifStyle}
                    >
                        “{t("hotelRental.philosophyQuote")}”
                    </blockquote>
                </section>

                {/* What's included — 6 tiles (light beige) */}
                <section
                    data-testid="hotel-rental-included"
                    className="bg-[#f3ead9]/50 py-20 md:py-28"
                >
                    <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16">
                        <p className="text-[11px] md:text-[12px] uppercase tracking-[0.4em] text-[#7a4a3a] mb-6">
                            {t("hotelRental.includedKicker")}
                        </p>
                        <h2
                            className="text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight text-[#1a1a18] max-w-3xl mb-14 md:mb-20"
                            style={serifStyle}
                        >
                            {t("hotelRental.includedTitle")}
                        </h2>

                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12 md:gap-y-16">
                            {includedItems.map((it, i) => (
                                <div
                                    key={i}
                                    data-testid={`hotel-rental-included-${i}`}
                                    className="border-t border-[#1a1a18]/15 pt-6"
                                >
                                    <h3
                                        className="text-xl md:text-2xl text-[#1a1a18] mb-3"
                                        style={serifStyle}
                                    >
                                        {it.title}
                                    </h3>
                                    <p className="text-[#3d3b36] text-[15px] leading-[1.75] font-light">
                                        {it.body}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Rental models — 3 columns */}
                <section
                    data-testid="hotel-rental-models"
                    className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28"
                >
                    <p className="text-[12px] md:text-[14px] uppercase tracking-[0.35em] text-[#7a4a3a] mb-6">
                        {t("hotelRental.modelsKicker")}
                    </p>
                    <h2
                        className="text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight text-[#1a1a18] max-w-3xl mb-14 md:mb-20"
                        style={serifStyle}
                    >
                        {t("hotelRental.modelsTitle")}
                    </h2>

                    <div className="grid md:grid-cols-3 gap-8 md:gap-10">
                        {models.map((m, i) => (
                            <article
                                key={i}
                                data-testid={`hotel-rental-model-${i}`}
                                className="flex flex-col bg-[#fdfbf7] border border-[#e5d9bf] p-8 md:p-10"
                            >
                                <span className="text-[11px] uppercase tracking-[0.32em] text-[#7a4a3a] mb-4">
                                    0{i + 1}
                                </span>
                                <h3
                                    className="text-2xl md:text-[30px] text-[#1a1a18] leading-tight mb-6"
                                    style={serifStyle}
                                >
                                    {m.title}
                                </h3>
                                <p className="text-[#3d3b36] text-[15px] leading-[1.75] font-light mb-6">
                                    {m.body}
                                </p>
                                <p className="mt-auto text-[12px] text-[#7a4a3a] italic font-light leading-[1.6] border-t border-[#e5d9bf] pt-5">
                                    {m.note}
                                </p>
                            </article>
                        ))}
                    </div>
                </section>

                {/* Hotel facilities & experiences */}
                <section
                    data-testid="hotel-rental-facilities"
                    className="bg-[#f3ead9]/50 py-20 md:py-28"
                >
                    <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16">
                        <p className="text-[12px] md:text-[14px] uppercase tracking-[0.35em] text-[#7a4a3a] mb-6">
                            {t("hotelRental.facilities.kicker")}
                        </p>
                        <h2
                            className="text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight text-[#1a1a18] max-w-3xl"
                            style={serifStyle}
                        >
                            {t("hotelRental.facilities.heading")}
                        </h2>
                        <p className="mt-6 max-w-2xl text-[#3d3b36] text-base md:text-[17px] leading-[1.85] font-light">
                            {t("hotelRental.facilities.intro")}
                        </p>

                        {/* Dining & shared spaces */}
                        <div className="mt-16 md:mt-20 grid lg:grid-cols-[1fr_1.2fr] gap-10 lg:gap-16 items-start">
                            <div>
                                <p className="text-[11px] md:text-[12px] uppercase tracking-[0.32em] text-[#7a4a3a] mb-4">
                                    {t("hotelRental.facilities.diningKicker")}
                                </p>
                                <p className="text-[#3d3b36] text-base md:text-[16px] leading-[1.85] font-light italic">
                                    {t("hotelRental.facilities.diningLede")}
                                </p>
                            </div>
                            <div className="grid grid-cols-2 gap-x-6 gap-y-8">
                                {t("hotelRental.facilities.spaces", { returnObjects: true }).map((s, i) => (
                                    <div
                                        key={i}
                                        data-testid={`hotel-facility-space-${i}`}
                                        className="border-t border-[#1a1a18]/15 pt-5"
                                    >
                                        <h3
                                            className="text-lg md:text-xl text-[#1a1a18] leading-tight"
                                            style={serifStyle}
                                        >
                                            {s.title}
                                        </h3>
                                        <p className="mt-1 text-[12px] uppercase tracking-[0.24em] text-[#7a4a3a] font-light">
                                            {s.meta}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* 3 pillars — Earth / Ocean / Morabeza */}
                        <div className="mt-24 md:mt-32">
                            <p className="text-[11px] md:text-[12px] uppercase tracking-[0.32em] text-[#7a4a3a] mb-10 md:mb-14">
                                {t("hotelRental.facilities.pillarsKicker")}
                            </p>
                            <div className="grid md:grid-cols-3 gap-8 md:gap-10">
                                {t("hotelRental.facilities.pillars", { returnObjects: true }).map((p, i) => (
                                    <article
                                        key={i}
                                        data-testid={`hotel-facility-pillar-${i}`}
                                        className="flex flex-col bg-[#fdfbf7] border border-[#e5d9bf] p-8 md:p-10 min-h-[280px]"
                                    >
                                        <span className="text-[11px] uppercase tracking-[0.38em] text-[#b59565] mb-5">
                                            {p.label}
                                        </span>
                                        <h3
                                            className="text-xl md:text-2xl text-[#1a1a18] leading-tight mb-5"
                                            style={serifStyle}
                                        >
                                            {p.title}
                                        </h3>
                                        <p className="text-[#3d3b36] text-[15px] leading-[1.75] font-light">
                                            {p.body}
                                        </p>
                                    </article>
                                ))}
                            </div>
                        </div>

                        {/* Amenities + On-demand */}
                        <div className="mt-24 md:mt-32 grid md:grid-cols-2 gap-12 md:gap-16">
                            <div>
                                <p className="text-[11px] md:text-[12px] uppercase tracking-[0.32em] text-[#7a4a3a] pb-4 border-b border-[#1a1a18]/15 mb-6">
                                    {t("hotelRental.facilities.activityKicker")}
                                </p>
                                <ul className="space-y-4">
                                    {t("hotelRental.facilities.amenities", { returnObjects: true }).map((a, i) => (
                                        <li
                                            key={i}
                                            data-testid={`hotel-facility-amenity-${i}`}
                                            className="flex gap-3 text-[#3d3b36] text-base md:text-[16px] leading-[1.65] font-light"
                                        >
                                            <span className="mt-3 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-[#7a4a3a]" />
                                            <span>{a}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div>
                                <p className="text-[11px] md:text-[12px] uppercase tracking-[0.32em] text-[#7a4a3a] pb-4 border-b border-[#1a1a18]/15 mb-6">
                                    {t("hotelRental.facilities.onDemandKicker")}
                                </p>
                                <ul className="grid grid-cols-2 gap-x-5 gap-y-3">
                                    {t("hotelRental.facilities.onDemand", { returnObjects: true }).map((a, i) => (
                                        <li
                                            key={i}
                                            data-testid={`hotel-facility-ondemand-${i}`}
                                            className="text-[#3d3b36] text-[15px] leading-[1.6] font-light"
                                        >
                                            {a}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA */}
                <section
                    data-testid="hotel-rental-cta"
                    className="bg-[#f3ead9] py-20 md:py-28"
                >
                    <div className="max-w-3xl mx-auto px-6 md:px-12 lg:px-16 text-center">
                        <p className="text-[12px] md:text-[14px] uppercase tracking-[0.4em] text-[#7a4a3a] mb-6">
                            {t("hotelRental.ctaKicker")}
                        </p>
                        <h2
                            className="text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#1a1a18]"
                            style={serifStyle}
                        >
                            {t("hotelRental.ctaTitle")}
                        </h2>
                        <p className="mt-6 text-[#3d3b36] text-base md:text-[17px] leading-[1.85] font-light">
                            {t("hotelRental.ctaDesc")}
                        </p>
                        <p className="mt-10 text-[12px] uppercase tracking-[0.28em] text-[#5c5a55] font-light">
                            {t("hotelRental.responseTime")}
                        </p>
                    </div>
                </section>
            </main>
        </PageChrome>
    );
}



function VillaTypeDetail({ config }) {
    useSEO(config.ns);
    const { t } = useTranslation();
    const serifStyle = {
        fontFamily: '"Cormorant Garamond", "Times New Roman", serif',
    };

    const { ns, testId, displayTitle, price, assetsBase, planPdf } = config;
    const features = ["windows", "kitchen", "bedrooms", "bathrooms", "terraces", "pool", "finish"];

    return (
        <PageChrome>
            <main
                data-testid={`${testId}-page`}
                className="min-h-screen bg-[#fdfbf7] text-[#1a1a18]"
            >
                {/* Hero */}
                <section className="relative">
                    <div className="relative w-full aspect-[16/10] md:aspect-[21/9] overflow-hidden bg-[#f3ead9]">
                        <img
                            src={`${assetsBase}/hero.jpg`}
                            alt={`${displayTitle} — Barefoot Luxury Cabo Verde`}
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a18]/10 via-transparent to-[#1a1a18]/50 pointer-events-none" />
                        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 lg:p-16">
                            <p className="text-[11px] md:text-[12px] uppercase tracking-[0.32em] text-[#f5ecd9]/90 mb-4">
                                {t(`${ns}.typeLabel`)}
                            </p>
                            <h1
                                data-testid={`${testId}-title`}
                                className="text-white text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[1] tracking-tight"
                                style={serifStyle}
                            >
                                {displayTitle}
                            </h1>
                        </div>
                    </div>
                </section>

                {/* Back link + Description | Plan side-by-side */}
                <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-14 md:pt-20 pb-16 md:pb-20">
                    <Link
                        to="/villas-for-sale"
                        data-testid={`${testId}-back-link`}
                        className="inline-flex items-center gap-2 text-[11px] md:text-[12px] uppercase tracking-[0.28em] text-[#5c5a55] hover:text-[#1a1a18] transition-colors mb-10 md:mb-14"
                    >
                        <ArrowLeft size={14} strokeWidth={1.4} />
                        {t(`${ns}.backToVillas`)}
                    </Link>

                    <div className="grid lg:grid-cols-[1fr_1.7fr] gap-10 lg:gap-16 items-start">
                        {/* Left — text */}
                        <div>
                            <p className="text-[12px] md:text-[14px] uppercase tracking-[0.35em] text-[#7a4a3a] mb-6">
                                {t(`${ns}.kicker`)}
                            </p>
                            <div
                                data-testid={`${testId}-description`}
                                className="space-y-6 text-[#3d3b36] text-base md:text-[17px] leading-[1.85] font-light"
                                style={{ fontFeatureSettings: '"liga", "kern"' }}
                            >
                                <p>{t(`${ns}.p1`)}</p>
                                <p>{t(`${ns}.p2`)}</p>
                            </div>

                            {/* Price + CTAs */}
                            <div className="mt-10 md:mt-12 border-t border-[#e5d9bf] pt-8">
                                <p className="text-[11px] uppercase tracking-[0.32em] text-[#7a4a3a] mb-2">
                                    {t(`${ns}.priceLabel`)}
                                </p>
                                <p className="text-4xl md:text-5xl text-[#1a1a18]" style={serifStyle}>
                                    {price}
                                </p>
                                <p className="mt-2 text-[13px] text-[#5c5a55] italic font-light">
                                    {t(`${ns}.plotNotIncluded`)}
                                </p>

                                <div className="mt-7 flex flex-wrap gap-3">
                                    <Link
                                        to="/contact"
                                        data-testid={`${testId}-enquire`}
                                        className="group inline-flex items-center gap-3 px-7 py-4 rounded-full bg-[#1a1a18] text-[#f5ecd9] text-[11px] md:text-[12px] uppercase tracking-[0.32em] hover:bg-[#b59565] hover:text-[#1a1a18] transition-colors duration-500"
                                    >
                                        {t("common.enquire")}
                                        <ArrowRight size={14} strokeWidth={1.6} className="transition-transform duration-500 group-hover:translate-x-1" />
                                    </Link>
                                    <a
                                        href={`${assetsBase}/${planPdf}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`${testId}-download-plan`}
                                        className="group inline-flex items-center gap-3 px-7 py-4 rounded-full border border-[#1a1a18] text-[#1a1a18] text-[11px] md:text-[12px] uppercase tracking-[0.32em] hover:bg-[#1a1a18] hover:text-[#f5ecd9] transition-colors duration-500"
                                    >
                                        <Download size={14} strokeWidth={1.6} />
                                        {t(`${ns}.downloadPlan`)}
                                    </a>
                                </div>
                            </div>
                        </div>

                        {/* Right — floor plan */}
                        <div data-testid={`${testId}-plan-section`}>
                            <p className="text-[11px] md:text-[12px] uppercase tracking-[0.32em] text-[#7a4a3a] mb-4">
                                {t(`${ns}.planKicker`)}
                            </p>
                            <a
                                href={`${assetsBase}/plan-full-hd.jpg`}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`${testId}-plan-image-link`}
                                className="block bg-white shadow-[0_20px_60px_rgba(26,26,24,0.10)] overflow-hidden"
                            >
                                <img
                                    src={`${assetsBase}/plan-full.jpg`}
                                    alt={`${displayTitle} — architectural floor plan`}
                                    className="w-full h-auto object-contain"
                                    loading="lazy"
                                />
                            </a>
                            <p className="mt-4 text-[11px] md:text-[12px] text-[#5c5a55] italic font-light leading-[1.7]">
                                {t(`${ns}.planDisclaimer`)}
                            </p>
                        </div>
                    </div>
                </section>

                {/* Features */}
                <section
                    data-testid={`${testId}-features`}
                    className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 pb-20 md:pb-28"
                >
                    <p className="text-[12px] md:text-[14px] uppercase tracking-[0.35em] text-[#7a4a3a] mb-10 text-center">
                        {t(`${ns}.atAGlance`)}
                    </p>
                    <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-x-6 gap-y-12 md:gap-y-14">
                        {features.map((key, i) => (
                            <li
                                key={key}
                                data-testid={`${testId}-feature-${i}`}
                                className="flex flex-col items-center text-center"
                            >
                                <img
                                    src={`/assets/villa-24/icons/${key}.svg`}
                                    alt=""
                                    aria-hidden="true"
                                    className="h-16 md:h-20 w-auto mb-5 select-none"
                                    draggable="false"
                                />
                                <p className="text-[12px] md:text-[13px] leading-[1.5] text-[#1a1a18] font-light lowercase">
                                    {t(`${ns}.features.${key}`)}
                                </p>
                            </li>
                        ))}
                    </ul>
                </section>
            </main>
        </PageChrome>
    );
}

const VILLA_TYPE_1_CONFIG = {
    ns: "villaType1",
    testId: "villa-type-1",
    displayTitle: "Villa Type 1",
    price: "731.000 €",
    assetsBase: "/assets/villa-type-1",
    planPdf: "BL-Plan-Villa-T1-EN-2026.pdf",
};
const VILLA_TYPE_2_CONFIG = {
    ns: "villaType2",
    testId: "villa-type-2",
    displayTitle: "Villa Type 2",
    price: "713.000 €",
    assetsBase: "/assets/villa-type-2",
    planPdf: "BL-Plan-Villa-T2-EN-2026.pdf",
};
const VILLA_TYPE_3_CONFIG = {
    ns: "villaType3",
    testId: "villa-type-3",
    displayTitle: "Villa Type 3",
    price: "739.000 €",
    assetsBase: "/assets/villa-type-3",
    planPdf: "BL-Plan-Villa-T3-EN-2026.pdf",
};
const VILLA_TYPE_4_CONFIG = {
    ns: "villaType4",
    testId: "villa-type-4",
    displayTitle: "Villa Type 4",
    price: "668.000 €",
    assetsBase: "/assets/villa-type-4",
    planPdf: "BL-Plan-Villa-T4-EN-2026.pdf",
};

const VillaType1 = () => (
    <VillaTypeDetail config={VILLA_TYPE_1_CONFIG} />
);

const VillaType2 = () => (
    <VillaTypeDetail config={VILLA_TYPE_2_CONFIG} />
);

const VillaType3 = () => (
    <VillaTypeDetail config={VILLA_TYPE_3_CONFIG} />
);

const VillaType4 = () => (
    <VillaTypeDetail config={VILLA_TYPE_4_CONFIG} />
);

const VILLA_TYPE_5_CONFIG = {
    ns: "villaType5",
    testId: "villa-type-5",
    displayTitle: "Villa Type 5",
    price: "555.000 €",
    assetsBase: "/assets/villa-type-5",
    planPdf: "BL-Plan-Villa-T5-EN-2026.pdf",
};

const VillaType5 = () => (
    <VillaTypeDetail config={VILLA_TYPE_5_CONFIG} />
);



const VILLA_24_PHOTOS = Array.from({ length: 22 }, (_, i) => `/assets/villa-24/photos/photo-${String(i + 1).padStart(2, "0")}.jpg`).filter((src) => !src.endsWith("photo-13.jpg"));

function Villa24() {
    useSEO("villa24");
    const { t } = useTranslation();
    const serifStyle = {
        fontFamily: '"Cormorant Garamond", "Times New Roman", serif',
    };

    const [current, setCurrent] = useState(0);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const total = VILLA_24_PHOTOS.length;
    const next = () => setCurrent((c) => (c + 1) % total);
    const prev = () => setCurrent((c) => (c - 1 + total) % total);

    // Keyboard arrows for a11y
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "ArrowRight") next();
            else if (e.key === "ArrowLeft") prev();
            else if (e.key === "Escape" && lightboxOpen) setLightboxOpen(false);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [lightboxOpen]);

    // Lock body scroll when lightbox is open
    useEffect(() => {
        if (!lightboxOpen) return;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prevOverflow; };
    }, [lightboxOpen]);

    // Preload the neighbouring image to smooth navigation
    useEffect(() => {
        const nextIdx = (current + 1) % total;
        const img = new Image();
        img.src = VILLA_24_PHOTOS[nextIdx];
    }, [current, total]);

    const features = ["windows", "kitchen", "bedrooms", "bathrooms", "terraces", "pool", "finish"];

    return (
        <PageChrome>
            <main
                data-testid="villa-24-page"
                className="min-h-screen bg-[#fdfbf7] text-[#1a1a18]"
            >
                {/* Hero Carousel */}
                <section
                    data-testid="villa-24-hero-carousel"
                    className="relative"
                    aria-roledescription="carousel"
                    aria-label="Villa 24 — photo gallery"
                >
                    <div className="relative w-full aspect-[16/10] md:aspect-[21/9] overflow-hidden bg-[#f3ead9]">
                        {VILLA_24_PHOTOS.map((src, i) => (
                            <img
                                key={src}
                                src={src}
                                alt={`Villa 24 — photo ${i + 1} of ${total}`}
                                loading={i === 0 ? "eager" : "lazy"}
                                data-testid={`villa-24-hero-slide-${i}`}
                                onClick={() => i === current && setLightboxOpen(true)}
                                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[900ms] ease-out ${
                                    i === current ? "opacity-100 cursor-zoom-in" : "opacity-0 pointer-events-none"
                                }`}
                                aria-hidden={i !== current}
                            />
                        ))}
                        <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a18]/20 via-transparent to-[#1a1a18]/50 pointer-events-none" />

                        {/* Title overlay */}
                        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 lg:p-16 pointer-events-none">
                            <p className="text-[11px] md:text-[12px] uppercase tracking-[0.32em] text-[#f5ecd9]/90 mb-4">
                                {t("villa24.typeLabel")}
                            </p>
                            <h1
                                data-testid="villa-24-title"
                                className="text-white text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[1] tracking-tight"
                                style={serifStyle}
                            >
                                Villa 24
                            </h1>
                        </div>

                        {/* Prev / Next arrows */}
                        <button
                            type="button"
                            onClick={prev}
                            data-testid="villa-24-hero-prev"
                            aria-label="Previous photo"
                            className="group absolute left-3 sm:left-5 md:left-8 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-11 h-11 md:w-14 md:h-14 rounded-full bg-white/25 hover:bg-white/90 backdrop-blur-sm text-white hover:text-[#1a1a18] transition-all duration-300"
                        >
                            <ArrowLeft size={20} strokeWidth={1.6} />
                        </button>
                        <button
                            type="button"
                            onClick={next}
                            data-testid="villa-24-hero-next"
                            aria-label="Next photo"
                            className="group absolute right-3 sm:right-5 md:right-8 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-11 h-11 md:w-14 md:h-14 rounded-full bg-white/25 hover:bg-white/90 backdrop-blur-sm text-white hover:text-[#1a1a18] transition-all duration-300"
                        >
                            <ArrowRight size={20} strokeWidth={1.6} />
                        </button>

                        {/* Counter */}
                        <div
                            data-testid="villa-24-hero-counter"
                            className="absolute top-4 right-4 md:top-6 md:right-6 z-10 px-3 py-1.5 rounded-full bg-[#1a1a18]/45 backdrop-blur-sm text-white text-[10px] md:text-[11px] uppercase tracking-[0.28em] font-light"
                            aria-live="polite"
                        >
                            {String(current + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
                        </div>

                        {/* Progress bar */}
                        <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-white/15 z-10">
                            <div
                                className="h-full bg-[#f5ecd9] transition-[width] duration-500 ease-out"
                                style={{ width: `${((current + 1) / total) * 100}%` }}
                                aria-hidden="true"
                            />
                        </div>
                    </div>
                </section>

                {/* Back link + Intro */}
                {/* Back link + Description | Plan side-by-side */}
                <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-14 md:pt-20 pb-16 md:pb-20">
                    <Link
                        to="/turnkey-villa"
                        data-testid="villa-24-back-link"
                        className="inline-flex items-center gap-2 text-[11px] md:text-[12px] uppercase tracking-[0.28em] text-[#5c5a55] hover:text-[#1a1a18] transition-colors mb-10 md:mb-14"
                    >
                        <ArrowLeft size={14} strokeWidth={1.4} />
                        {t("villa24.backToTurnkey")}
                    </Link>

                    <div className="grid lg:grid-cols-[1fr_1.7fr] gap-10 lg:gap-16 items-start">
                        {/* Left — text */}
                        <div>
                            <p className="text-[12px] md:text-[14px] uppercase tracking-[0.35em] text-[#7a4a3a] mb-6">
                                {t("villa24.kicker")}
                            </p>
                            <div
                                data-testid="villa-24-description"
                                className="space-y-6 text-[#3d3b36] text-base md:text-[17px] leading-[1.85] font-light"
                                style={{ fontFeatureSettings: '"liga", "kern"' }}
                            >
                                <p>{t("villa24.p1")}</p>
                                <p>{t("villa24.p2")}</p>
                            </div>

                            {/* Price + CTAs */}
                            <div className="mt-10 md:mt-12 border-t border-[#e5d9bf] pt-8">
                                <p className="text-[11px] uppercase tracking-[0.32em] text-[#7a4a3a] mb-2">
                                    {t("villa24.priceLabel")}
                                </p>
                                <p className="text-4xl md:text-5xl text-[#1a1a18]" style={serifStyle}>
                                    895.000 €
                                </p>
                                <p className="mt-2 text-[13px] text-[#5c5a55] italic font-light">
                                    {t("villa24.plotIncluded")}
                                </p>

                                <div className="mt-7 flex flex-wrap gap-3">
                                    <Link
                                        to="/contact"
                                        data-testid="villa-24-enquire"
                                        className="group inline-flex items-center gap-3 px-7 py-4 rounded-full bg-[#1a1a18] text-[#f5ecd9] text-[11px] md:text-[12px] uppercase tracking-[0.32em] hover:bg-[#b59565] hover:text-[#1a1a18] transition-colors duration-500"
                                    >
                                        {t("common.enquire")}
                                        <ArrowRight size={14} strokeWidth={1.6} className="transition-transform duration-500 group-hover:translate-x-1" />
                                    </Link>
                                    <a
                                        href="/assets/villa-24/BL-Plan-Villa-T2-EN-2026.pdf"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="villa-24-download-plan"
                                        className="group inline-flex items-center gap-3 px-7 py-4 rounded-full border border-[#1a1a18] text-[#1a1a18] text-[11px] md:text-[12px] uppercase tracking-[0.32em] hover:bg-[#1a1a18] hover:text-[#f5ecd9] transition-colors duration-500"
                                    >
                                        <Download size={14} strokeWidth={1.6} />
                                        {t("villa24.downloadPlan")}
                                    </a>
                                </div>
                            </div>
                        </div>

                        {/* Right — floor plan */}
                        <div data-testid="villa-24-plan-section">
                            <p className="text-[11px] md:text-[12px] uppercase tracking-[0.32em] text-[#7a4a3a] mb-4">
                                {t("villa24.planKicker")}
                            </p>
                            <a
                                href="/assets/villa-24/plan-full-hd.jpg"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="villa-24-plan-image-link"
                                className="block bg-white shadow-[0_20px_60px_rgba(26,26,24,0.10)] overflow-hidden"
                            >
                                <img
                                    src="/assets/villa-24/plan-full.jpg"
                                    alt="Villa 24 — Type 2 architectural floor plan (Level 0 & Level -1)"
                                    className="w-full h-auto object-contain"
                                    loading="lazy"
                                />
                            </a>
                            <p className="mt-4 text-[11px] md:text-[12px] text-[#5c5a55] italic font-light leading-[1.7]">
                                {t("villa24.planDisclaimer")}
                            </p>
                        </div>
                    </div>
                </section>

                {/* Features */}
                <section
                    data-testid="villa-24-features"
                    className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 pb-20 md:pb-28"
                >
                    <p className="text-[12px] md:text-[14px] uppercase tracking-[0.35em] text-[#7a4a3a] mb-10 text-center">
                        {t("villa24.atAGlance")}
                    </p>
                    <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-x-6 gap-y-12 md:gap-y-14">
                        {features.map((key, i) => (
                            <li
                                key={key}
                                data-testid={`villa-24-feature-${i}`}
                                className="flex flex-col items-center text-center"
                            >
                                <img
                                    src={`/assets/villa-24/icons/${key}.svg`}
                                    alt=""
                                    aria-hidden="true"
                                    className="h-16 md:h-20 w-auto mb-5 select-none"
                                    draggable="false"
                                />
                                <p className="text-[12px] md:text-[13px] leading-[1.5] text-[#1a1a18] font-light lowercase">
                                    {t(`villa24.features.${key}`)}
                                </p>
                            </li>
                        ))}
                    </ul>
                </section>
            </main>

            {/* Lightbox */}
            {lightboxOpen && (
                <div
                    data-testid="villa-24-lightbox"
                    role="dialog"
                    aria-modal="true"
                    aria-label={`Villa 24 — photo ${current + 1} of ${total}`}
                    className="fixed inset-0 z-[100] bg-[#0a0a09]/95 backdrop-blur-md flex items-center justify-center animate-in fade-in duration-300"
                    onClick={() => setLightboxOpen(false)}
                >
                    <button
                        type="button"
                        data-testid="villa-24-lightbox-close"
                        onClick={(e) => { e.stopPropagation(); setLightboxOpen(false); }}
                        aria-label="Close"
                        className="absolute top-4 right-4 md:top-6 md:right-6 z-10 w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-colors"
                    >
                        <X size={22} strokeWidth={1.6} />
                    </button>
                    <button
                        type="button"
                        data-testid="villa-24-lightbox-prev"
                        onClick={(e) => { e.stopPropagation(); prev(); }}
                        aria-label="Previous photo"
                        className="absolute left-3 sm:left-6 md:left-10 top-1/2 -translate-y-1/2 z-10 w-11 h-11 md:w-14 md:h-14 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-colors"
                    >
                        <ArrowLeft size={22} strokeWidth={1.6} />
                    </button>
                    <button
                        type="button"
                        data-testid="villa-24-lightbox-next"
                        onClick={(e) => { e.stopPropagation(); next(); }}
                        aria-label="Next photo"
                        className="absolute right-3 sm:right-6 md:right-10 top-1/2 -translate-y-1/2 z-10 w-11 h-11 md:w-14 md:h-14 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-colors"
                    >
                        <ArrowRight size={22} strokeWidth={1.6} />
                    </button>
                    <figure
                        className="relative max-w-[92vw] max-h-[88vh] flex flex-col items-center"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <img
                            src={VILLA_24_PHOTOS[current]}
                            alt={`Villa 24 — photo ${current + 1} of ${total}`}
                            data-testid="villa-24-lightbox-image"
                            className="max-w-full max-h-[80vh] w-auto h-auto object-contain shadow-2xl"
                        />
                        <figcaption className="mt-4 text-center text-white/70 text-[11px] md:text-[12px] uppercase tracking-[0.28em]">
                            <span data-testid="villa-24-lightbox-counter">{String(current + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}</span>
                        </figcaption>
                    </figure>
                </div>
            )}
        </PageChrome>
    );
}


function ScrollToTop() {
    const { pathname } = useLocation();
    const basePath = stripLangPrefix(pathname);
    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }, [basePath]);
    return null;
}

function LangSync() {
    const { pathname } = useLocation();
    const { i18n } = useTranslation();
    useEffect(() => {
        const urlLang = langFromPath(pathname);
        if ((i18n.language || "en").slice(0, 2) !== urlLang) i18n.changeLanguage(urlLang);
    }, [pathname, i18n]);
    return null;
}

function PolicyPage({ testid, kicker, title, sections }) {
    const { t } = useTranslation();
    const serifStyle = {
        fontFamily: '"Cormorant Garamond", "Times New Roman", serif',
    };
    return (
        <PageChrome><main
            data-testid={testid}
            className="min-h-screen bg-[#fdfbf7] text-[#1a1a18]"
        >
            <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-16 pt-32 md:pt-44 pb-24 md:pb-32">
                <Link
                    to="/"
                    data-testid={`${testid}-back-link`}
                    className="inline-flex items-center gap-2 text-[11px] md:text-[12px] uppercase tracking-[0.28em] text-[#5c5a55] hover:text-[#1a1a18] transition-colors mb-12"
                >
                    <ArrowLeft size={14} strokeWidth={1.4} />
                    {t("common.backToHome")}
                </Link>

                {kicker && (
                    <p className="text-[12px] md:text-[14px] uppercase tracking-[0.35em] text-[#7a4a3a] mb-6">
                        — {kicker}
                    </p>
                )}
                <h1
                    className="text-4xl sm:text-5xl md:text-6xl lg:text-[64px] leading-[1.05] tracking-tight text-balance"
                    style={serifStyle}
                >
                    {title}
                </h1>

                <div className="mt-14 md:mt-20 space-y-14 md:space-y-16 text-[#3d3b36] text-[15px] md:text-[16px] leading-[1.85] font-light">
                    {sections.map((s, si) => (
                        <section key={si}>
                            {s.heading && (
                                <h2
                                    className="text-xl md:text-2xl leading-[1.2] tracking-tight text-[#1a1a18] mb-6"
                                    style={serifStyle}
                                >
                                    {s.heading}
                                </h2>
                            )}
                            <div className="space-y-5">
                                {s.blocks.map((b, bi) => {
                                    if (b.type === "p") return <p key={bi}>{b.text}</p>;
                                    if (b.type === "ul")
                                        return (
                                            <ul key={bi} className="list-disc pl-6 space-y-3 marker:text-[#b59565]">
                                                {b.items.map((it, ii) => (
                                                    <li key={ii}>{it}</li>
                                                ))}
                                            </ul>
                                        );
                                    if (b.type === "table")
                                        return (
                                            <div key={bi} className="space-y-2">
                                                {b.caption && (
                                                    <p className="text-[12px] uppercase tracking-[0.2em] text-[#7a4a3a]">
                                                        {b.caption}
                                                    </p>
                                                )}
                                                <div className="overflow-x-auto border border-[#e5d9bf]">
                                                    <table className="w-full border-collapse text-[13px] md:text-[14px]">
                                                        <thead>
                                                            <tr className="bg-[#f3ead9] text-left text-[#1a1a18]">
                                                                {b.headers.map((h, hi) => (
                                                                    <th key={hi} className="px-4 py-3 font-normal border-b border-[#e5d9bf] align-top">
                                                                        {h}
                                                                    </th>
                                                                ))}
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {b.rows.map((row, ri) => (
                                                                <tr key={ri} className="border-b border-[#e5d9bf] last:border-b-0">
                                                                    {row.map((cell, ci) => (
                                                                        <td key={ci} className="px-4 py-3 align-top whitespace-nowrap md:whitespace-normal">
                                                                            {ci === 0 ? <code className="text-[12px] text-[#7a4a3a]">{cell}</code> : cell}
                                                                        </td>
                                                                    ))}
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        );
                                    return null;
                                })}
                            </div>
                        </section>
                    ))}
                </div>
            </div>
        </main></PageChrome>
    );
}

function LegalPage({ k, testid }) {
    useSEO(k === "terms" ? "terms" : k);
    const { t } = useTranslation();
    const data = t(`legal.${k}`, { returnObjects: true });
    return (
        <PolicyPage
            testid={testid}
            kicker={data.kicker}
            title={data.title}
            sections={data.sections}
        />
    );
}

function CookiePolicy() { return <LegalPage k="cookiePolicy" testid="cookie-policy-page" />; }
function PrivacyPolicy() { return <LegalPage k="privacyPolicy" testid="privacy-policy-page" />; }
function TermsAndConditions() { return <LegalPage k="terms" testid="terms-page" />; }
function Disclaimer() { return <LegalPage k="disclaimer" testid="disclaimer-page" />; }

function NotFound() {
    useSEO("notFound", { noindex: true });
    const { t } = useTranslation();
    const serifStyle = { fontFamily: '"Cormorant Garamond", "Times New Roman", serif' };
    return (
        <PageChrome>
            <main data-testid="not-found-page" className="min-h-[70vh] bg-[#fdfbf7] text-[#1a1a18] flex items-center">
                <div className="max-w-3xl mx-auto px-6 md:px-10 py-24 md:py-32 text-center">
                    <p className="text-[11px] md:text-[12px] uppercase tracking-[0.32em] text-[#7a4a3a] font-light mb-6">
                        — {t("notFound.kicker")}
                    </p>
                    <p className="text-[90px] md:text-[130px] leading-none text-[#e5d9bf] select-none" style={serifStyle} aria-hidden="true">
                        404
                    </p>
                    <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight" style={serifStyle}>
                        {t("notFound.title")}
                    </h1>
                    <p className="mt-6 text-[#5c5a55] text-base md:text-lg leading-[1.75] font-light max-w-xl mx-auto">
                        {t("notFound.text")}
                    </p>
                    <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
                        <Link
                            to="/villas-for-sale"
                            data-testid="not-found-villas-link"
                            className="inline-flex items-center gap-2 bg-[#1a1a18] hover:bg-[#7a4a3a] text-white px-8 py-4 text-[12px] uppercase tracking-[0.25em] font-light transition-colors"
                        >
                            {t("notFound.ctaVillas")}
                            <ArrowRight size={14} strokeWidth={1.4} />
                        </Link>
                        <Link
                            to="/"
                            data-testid="not-found-home-link"
                            className="text-[12px] uppercase tracking-[0.25em] font-light text-[#5c5a55] hover:text-[#1a1a18] underline underline-offset-4 decoration-[#cfc6b3] transition-colors"
                        >
                            {t("notFound.ctaHome")}
                        </Link>
                    </div>
                </div>
            </main>
        </PageChrome>
    );
}

const PAGES = [
    ["", Landing],
    ["press", Press],
    ["gallery", Gallery],
    ["contact", Contact],
    ["about", About],
    ["destination", Destination],
    ["turnkey-villa", TurnkeyVilla],
    ["villas-for-sale", VillasForSale],
    ["villas-for-sale/type-1", VillaType1],
    ["villas-for-sale/type-2", VillaType2],
    ["villas-for-sale/type-3", VillaType3],
    ["villas-for-sale/type-4", VillaType4],
    ["villas-for-sale/type-5", VillaType5],
    ["hotel-rental-program", HotelRentalProgram],
    ["turnkey-villa/villa-24", Villa24],
    ["cookie-policy", CookiePolicy],
    ["privacy-policy", PrivacyPolicy],
    ["terms-and-conditions", TermsAndConditions],
    ["disclaimer", Disclaimer],
];

// Legacy URL redirects (301-equivalent via <Navigate replace>).
// Each entry maps an OLD path to the current canonical path. The pattern is
// applied for the language-less root and for every /fr, /nl, /pt prefix so
// that Google's cached indexes across locales are all consolidated.
const LEGACY_REDIRECTS = [
    // Old Dutch slug for /gallery (was live before the multilingual rewrite)
    ["galerij", "gallery"],
    // Old "About us" slug
    ["about-us", "about"],
    // Old villa type slugs (villa-type-01..05 → villas-for-sale/type-1..5)
    ["villa-type-01", "villas-for-sale/type-1"],
    ["villa-type-02", "villas-for-sale/type-2"],
    ["villa-type-03", "villas-for-sale/type-3"],
    ["villa-type-04", "villas-for-sale/type-4"],
    ["villa-type-05", "villas-for-sale/type-5"],
    ["villa-type-1", "villas-for-sale/type-1"],
    ["villa-type-2", "villas-for-sale/type-2"],
    ["villa-type-3", "villas-for-sale/type-3"],
    ["villa-type-4", "villas-for-sale/type-4"],
    ["villa-type-5", "villas-for-sale/type-5"],
    // Old Villa 24 slug attempts
    ["villa-24", "turnkey-villa/villa-24"],
    // Villa 26 is a sold unit listed on the turnkey villas page (no standalone page)
    ["villa-26", "turnkey-villa"],
    // Old SEO landing slug → real estate for-sale collection
    ["real-estate-cabo-verde", "villas-for-sale"],
    // English keyword landing slug → real estate for-sale collection
    ["cape-verde-property-for-sale", "villas-for-sale"],
    // Old Dutch "about us" slug (over-ons) → about (nl/over-ons → nl/about)
    ["over-ons", "about"],
    // WordPress RSS feed leftovers → home
    ["feed", ""],
];

// Absolute legacy redirects (exact old path → exact new path, NOT multiplied
// across language prefixes). Used for old WordPress single-language slugs that
// only ever existed at the root, e.g. the Dutch "villas te koop".
const ABSOLUTE_REDIRECTS = [
    ["/villas-te-koop", "/nl/villas-for-sale"],
    ["/nl/villas-te-koop", "/nl/villas-for-sale"],
];

/**
 * Strips a single trailing slash from any non-root URL. Prevents
 * `/foo/` and `/foo` being treated as duplicate pages by Google.
 */
function TrailingSlashRedirect() {
    const { pathname, search, hash } = useLocation();
    if (pathname !== "/" && pathname.length > 1 && pathname.endsWith("/")) {
        return <Navigate to={pathname.replace(/\/+$/, "") + search + hash} replace />;
    }
    return null;
}

function App() {
    return (
        <BrowserRouter>
            <ScrollToTop />
            <LangSync />
            <TrailingSlashRedirect />
            <Routes>
                {["", "fr", "nl", "pt"].flatMap((lng) =>
                    PAGES.map(([p, Page]) => (
                        <Route
                            key={`${lng}|${p}`}
                            path={`/${lng}${lng && p ? "/" : ""}${p}`}
                            element={<Page />}
                        />
                    ))
                )}
                {/* Legacy redirects — one per language prefix */}
                {["", "fr", "nl", "pt"].flatMap((lng) =>
                    LEGACY_REDIRECTS.map(([oldSlug, newSlug]) => {
                        const from = `/${lng}${lng ? "/" : ""}${oldSlug}`;
                        const to = `/${lng}${lng ? "/" : ""}${newSlug}`;
                        return (
                            <Route
                                key={`redir|${lng}|${oldSlug}`}
                                path={from}
                                element={<Navigate to={to} replace />}
                            />
                        );
                    })
                )}
                {/* Absolute legacy redirects (exact path, single language) */}
                {ABSOLUTE_REDIRECTS.map(([from, to]) => (
                    <Route
                        key={`absredir|${from}`}
                        path={from}
                        element={<Navigate to={to} replace />}
                    />
                ))}
                <Route path="*" element={<NotFound />} />
            </Routes>
            <CookieConsent />
        </BrowserRouter>
    );
}

export default App;
